import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  addEdge,
  useNodesState,
  useEdgesState,
  MarkerType,
  Panel,
  Handle,
  updateEdge
} from 'reactflow';
import 'reactflow/dist/style.css';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import {
  FaPlay,
  FaStop,
  FaSave,
  FaTrash,
  FaPlus,
  FaRobot,
  FaComment,
  FaClock,
  FaCodeBranch,
  FaBolt,
  FaGlobe,
  FaWhatsapp,
  FaInstagram,
  FaFacebook,
  FaGoogle,
  FaTimes,
  FaEdit,
  FaExclamationCircle,
  FaInfoCircle,
  FaComments,
  FaNetworkWired,
  FaSync,
  FaExchangeAlt,
  FaTag,
  FaExclamationTriangle,
  FaHistory,
  FaDatabase,
  FaTerminal
} from 'react-icons/fa';
import ConfirmModal from './common/ConfirmModal';
import WorkflowChat from './WorkflowChat';
import './WorkflowManager.css';

// Custom Node Components with Handles for connections
const TriggerNode = ({ data, selected }) => {
  const hasError = data.hasError || false;
  const hasOutput = data.hasOutput || false;
  const outputSuccess = data.outputSuccess !== false;
  return (
    <div className={`workflow-node trigger-node ${selected ? 'selected' : ''} ${hasError ? 'has-error' : ''}`}>
      <Handle type="source" position="right" style={{ background: '#ff9800' }} />
      <div className="node-header">
        <FaBolt className="node-icon" />
        <span className="node-title">{data.label || 'Trigger'}</span>
        {hasError && <FaExclamationCircle className="node-error-icon" title="This node has validation errors" />}
        {hasOutput && (
          <div 
            className="node-output-indicator" 
            title={`Click to view output - ${outputSuccess ? 'Success' : 'Error'}`}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: outputSuccess ? '#10b981' : '#ef4444',
              border: '2px solid white',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          />
        )}
      </div>
      <div className="node-body">
        <div className="node-info">{data.triggerType || data.platform || 'Manual'}</div>
        {data.platform && <div className="node-platform">{data.platform}</div>}
      </div>
    </div>
  );
};

const LLMNode = ({ data, selected }) => {
  const hasError = data.hasError || false;
  const hasOutput = data.hasOutput || false;
  const outputSuccess = data.outputSuccess !== false;
  return (
    <div className={`workflow-node llm-node ${selected ? 'selected' : ''} ${hasError ? 'has-error' : ''}`}>
      <Handle type="target" position="left" style={{ background: '#9c27b0' }} />
      <Handle type="source" position="right" style={{ background: '#9c27b0' }} />
      <div className="node-header">
        <FaRobot className="node-icon" />
        <span className="node-title">{data.label || 'AI Response'}</span>
        {hasError && <FaExclamationCircle className="node-error-icon" title="This node has validation errors" />}
        {hasOutput && (
          <div 
            className="node-output-indicator" 
            title={`Click to view output - ${outputSuccess ? 'Success' : 'Error'}`}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: outputSuccess ? '#10b981' : '#ef4444',
              border: '2px solid white',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          />
        )}
      </div>
      <div className="node-body">
        <div className="node-info">{data.model || 'GPT-3.5'}</div>
        {data.prompt && <div className="node-prompt">{data.prompt.substring(0, 50)}...</div>}
      </div>
    </div>
  );
};

const ActionNode = ({ data, selected }) => {
  const hasError = data.hasError || false;
  const hasOutput = data.hasOutput || false;
  const outputSuccess = data.outputSuccess !== false;
  return (
    <div className={`workflow-node action-node ${selected ? 'selected' : ''} ${hasError ? 'has-error' : ''}`}>
      <Handle type="target" position="left" style={{ background: '#2196f3' }} />
      <Handle type="source" position="right" style={{ background: '#2196f3' }} />
      <div className="node-header">
        <FaComment className="node-icon" />
        <span className="node-title">{data.label || 'Action'}</span>
        {hasError && <FaExclamationCircle className="node-error-icon" title="This node has validation errors" />}
        {hasOutput && (
          <div 
            className="node-output-indicator" 
            title={`Click to view output - ${outputSuccess ? 'Success' : 'Error'}`}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: outputSuccess ? '#10b981' : '#ef4444',
              border: '2px solid white',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          />
        )}
      </div>
      <div className="node-body">
        <div className="node-info">{data.actionType || 'Send Message'}</div>
        {data.platform && <div className="node-platform">{data.platform}</div>}
      </div>
    </div>
  );
};

const ConditionNode = ({ data, selected }) => {
  const hasError = data.hasError || false;
  const isActive = data.isActive !== false;
  const hasOutput = data.hasOutput || false;
  const outputSuccess = data.outputSuccess !== false;
  return (
    <div className={`workflow-node condition-node ${selected ? 'selected' : ''} ${!isActive ? 'inactive' : ''} ${hasError ? 'has-error' : ''}`}>
      <Handle type="target" position="left" style={{ background: '#4caf50' }} />
      <Handle type="source" position="right" id="true" style={{ background: '#4caf50', top: '30%' }} />
      <Handle type="source" position="right" id="false" style={{ background: '#4caf50', top: '70%' }} />
      <div className="node-header">
        <FaCodeBranch className="node-icon" />
        <span className="node-title">{data.label || data.conditionName || 'Condition'}</span>
        {hasError && <FaExclamationCircle className="node-error-icon" title="This node has validation errors" />}
        {hasOutput && (
          <div 
            className="node-output-indicator" 
            title={`Click to view output - ${outputSuccess ? 'Success' : 'Error'}`}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: outputSuccess ? '#10b981' : '#ef4444',
              border: '2px solid white',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          />
        )}
        {!isActive && <span className="node-status-badge inactive">Inactive</span>}
      </div>
      <div className="node-body">
        <div className="node-info">{data.conditionName || 'Condition 1'}</div>
        {data.condition && <div className="node-condition">{data.condition}</div>}
      </div>
    </div>
  );
};

const DelayNode = ({ data, selected }) => {
  const hasError = data.hasError || false;
  const hasOutput = data.hasOutput || false;
  const outputSuccess = data.outputSuccess !== false;
  return (
    <div className={`workflow-node delay-node ${selected ? 'selected' : ''} ${hasError ? 'has-error' : ''}`}>
      <Handle type="target" position="left" style={{ background: '#ff5722' }} />
      <Handle type="source" position="right" style={{ background: '#ff5722' }} />
      <div className="node-header">
        <FaClock className="node-icon" />
        <span className="node-title">{data.label || 'Delay'}</span>
        {hasError && <FaExclamationCircle className="node-error-icon" title="This node has validation errors" />}
        {hasOutput && (
          <div 
            className="node-output-indicator" 
            title={`Click to view output - ${outputSuccess ? 'Success' : 'Error'}`}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: outputSuccess ? '#10b981' : '#ef4444',
              border: '2px solid white',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          />
        )}
      </div>
      <div className="node-body">
        <div className="node-info">{data.delay || '0'} seconds</div>
      </div>
    </div>
  );
};

const HTTPNode = ({ data, selected }) => {
  const hasError = data.hasError || false;
  const hasOutput = data.hasOutput || false;
  const outputSuccess = data.outputSuccess !== false;
  return (
    <div className={`workflow-node http-node ${selected ? 'selected' : ''} ${hasError ? 'has-error' : ''}`}>
      <Handle type="target" position="left" style={{ background: '#00bcd4' }} />
      <Handle type="source" position="right" style={{ background: '#00bcd4' }} />
      <div className="node-header">
        <FaNetworkWired className="node-icon" />
        <span className="node-title">{data.label || 'HTTP Request'}</span>
        {hasError && <FaExclamationCircle className="node-error-icon" title="This node has validation errors" />}
        {hasOutput && (
          <div 
            className="node-output-indicator" 
            title={`Click to view output - ${outputSuccess ? 'Success' : 'Error'}`}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: outputSuccess ? '#10b981' : '#ef4444',
              border: '2px solid white',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          />
        )}
      </div>
      <div className="node-body">
        <div className="node-info">{data.method || 'GET'} {data.url ? data.url.substring(0, 30) + '...' : 'No URL'}</div>
      </div>
    </div>
  );
};

const LoopNode = ({ data, selected }) => {
  const hasError = data.hasError || false;
  const hasOutput = data.hasOutput || false;
  const outputSuccess = data.outputSuccess !== false;
  return (
    <div className={`workflow-node loop-node ${selected ? 'selected' : ''} ${hasError ? 'has-error' : ''}`}>
      <Handle type="target" position="left" style={{ background: '#9c27b0' }} />
      <Handle type="source" position="right" style={{ background: '#9c27b0' }} />
      <div className="node-header">
        <FaSync className="node-icon" />
        <span className="node-title">{data.label || 'Loop'}</span>
        {hasError && <FaExclamationCircle className="node-error-icon" title="This node has validation errors" />}
        {hasOutput && (
          <div 
            className="node-output-indicator" 
            title={`Click to view output - ${outputSuccess ? 'Success' : 'Error'}`}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: outputSuccess ? '#10b981' : '#ef4444',
              border: '2px solid white',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          />
        )}
      </div>
      <div className="node-body">
        <div className="node-info">{data.loopType || 'array'} loop</div>
      </div>
    </div>
  );
};

const TransformNode = ({ data, selected }) => {
  const hasError = data.hasError || false;
  const hasOutput = data.hasOutput || false;
  const outputSuccess = data.outputSuccess !== false;
  return (
    <div className={`workflow-node transform-node ${selected ? 'selected' : ''} ${hasError ? 'has-error' : ''}`}>
      <Handle type="target" position="left" style={{ background: '#ff9800' }} />
      <Handle type="source" position="right" style={{ background: '#ff9800' }} />
      <div className="node-header">
        <FaExchangeAlt className="node-icon" />
        <span className="node-title">{data.label || 'Transform'}</span>
        {hasError && <FaExclamationCircle className="node-error-icon" title="This node has validation errors" />}
        {hasOutput && (
          <div 
            className="node-output-indicator" 
            title={`Click to view output - ${outputSuccess ? 'Success' : 'Error'}`}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: outputSuccess ? '#10b981' : '#ef4444',
              border: '2px solid white',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          />
        )}
      </div>
      <div className="node-body">
        <div className="node-info">Data transformation</div>
      </div>
    </div>
  );
};

const VariableNode = ({ data, selected }) => {
  const hasError = data.hasError || false;
  const hasOutput = data.hasOutput || false;
  const outputSuccess = data.outputSuccess !== false;
  return (
    <div className={`workflow-node variable-node ${selected ? 'selected' : ''} ${hasError ? 'has-error' : ''}`}>
      <Handle type="target" position="left" style={{ background: '#4caf50' }} />
      <Handle type="source" position="right" style={{ background: '#4caf50' }} />
      <div className="node-header">
        <FaTag className="node-icon" />
        <span className="node-title">{data.label || 'Variable'}</span>
        {hasError && <FaExclamationCircle className="node-error-icon" title="This node has validation errors" />}
        {hasOutput && (
          <div 
            className="node-output-indicator" 
            title={`Click to view output - ${outputSuccess ? 'Success' : 'Error'}`}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: outputSuccess ? '#10b981' : '#ef4444',
              border: '2px solid white',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          />
        )}
      </div>
      <div className="node-body">
        <div className="node-info">{data.variableName || 'No variable'}</div>
      </div>
    </div>
  );
};

const ErrorNode = ({ data, selected }) => {
  const hasError = data.hasError || false;
  const hasOutput = data.hasOutput || false;
  const outputSuccess = data.outputSuccess !== false;
  return (
    <div className={`workflow-node error-node ${selected ? 'selected' : ''} ${hasError ? 'has-error' : ''}`}>
      <Handle type="target" position="left" style={{ background: '#f44336' }} />
      <Handle type="source" position="right" style={{ background: '#f44336' }} />
      <div className="node-header">
        <FaExclamationTriangle className="node-icon" />
        <span className="node-title">{data.label || 'Error Handler'}</span>
        {hasError && <FaExclamationCircle className="node-error-icon" title="This node has validation errors" />}
        {hasOutput && (
          <div 
            className="node-output-indicator" 
            title={`Click to view output - ${outputSuccess ? 'Success' : 'Error'}`}
            style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              backgroundColor: outputSuccess ? '#10b981' : '#ef4444',
              border: '2px solid white',
              cursor: 'pointer',
              marginLeft: 'auto'
            }}
          />
        )}
      </div>
      <div className="node-body">
        <div className="node-info">{data.errorAction || 'throw'}</div>
      </div>
    </div>
  );
};

const nodeTypes = {
  trigger: TriggerNode,
  llm: LLMNode,
  action: ActionNode,
  condition: ConditionNode,
  delay: DelayNode,
  http: HTTPNode,
  loop: LoopNode,
  transform: TransformNode,
  variable: VariableNode,
  error: ErrorNode
};

const WorkflowManager = () => {
  const [workflows, setWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState(null);
  const [workflowName, setWorkflowName] = useState('');
  const [workflowDescription, setWorkflowDescription] = useState('');
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showNodePanel, setShowNodePanel] = useState(true);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'danger'
  });
  const [showLLMConfig, setShowLLMConfig] = useState(false);
  const [llmConfig, setLlmConfig] = useState({
    provider: 'openrouter',
    models: [], // Array of selected models (in addition to default)
    temperature: 0.7,
    maxTokens: 500,
    systemPrompt: 'You are a helpful customer service assistant. Respond professionally and helpfully to customer inquiries.',
    useGlobalConfig: true
  });
  const [responseTemplates, setResponseTemplates] = useState([]);
  const [newTemplate, setNewTemplate] = useState({ keyword: '', response: '', useLLM: false, llmPrompt: '' });
  const [selectedNode, setSelectedNode] = useState(null);
  const [showNodeConfig, setShowNodeConfig] = useState(false);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [showEdgeDeleteConfirm, setShowEdgeDeleteConfirm] = useState(false);
  const [pendingEdgeDeletion, setPendingEdgeDeletion] = useState(null);
  const [confirmedDeletions, setConfirmedDeletions] = useState(new Set());
  const [finalResponse, setFinalResponse] = useState(null);
  const [workflowExecutionStatus, setWorkflowExecutionStatus] = useState(null);
  const [workflowValidation, setWorkflowValidation] = useState({ valid: false, message: '', errors: [] });
  const [nodeErrors, setNodeErrors] = useState(new Map()); // Map of nodeId -> array of errors
  const [nodeOutputs, setNodeOutputs] = useState(new Map()); // Map of nodeId -> output data
  const [showNodeOutput, setShowNodeOutput] = useState(null); // Currently selected node for output view
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [expressionHelper, setExpressionHelper] = useState({ show: false, fieldRef: null, fieldType: null });
  const [aiConfig, setAiConfig] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [executionHistory, setExecutionHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);

  const token = localStorage.getItem('token');
  const reactFlowWrapper = useRef(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);

  useEffect(() => {
    fetchWorkflows();
    fetchAIConfig();
  }, []);

  useEffect(() => {
    if (selectedWorkflow?._id && showHistory) {
      fetchExecutionHistory();
    }
  }, [selectedWorkflow?._id, showHistory]);

  useEffect(() => {
    if (selectedWorkflow) {
      // Reset nodes and edges before loading new workflow to prevent ResizeObserver issues
      setNodes([]);
      setEdges([]);
      setSelectedNode(null);
      setShowNodeConfig(false);
      // Small delay to ensure ReactFlow is ready
      setTimeout(() => {
        loadWorkflow(selectedWorkflow);
      }, 100);
    }
  }, [selectedWorkflow]);

  const fetchAIConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ai-config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setAiConfig(response.data.data);
        // Auto-populate LLM config from OpenRouter settings if available
        // OpenRouter config is always loaded from AI Configuration Manager
        if (response.data.data?.openrouter) {
          setLlmConfig(prev => ({
            ...prev,
            provider: 'openrouter',
            models: prev.models || [], // Keep existing models, don't override
            useGlobalConfig: true
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching AI config:', error);
    }
  };

  const fetchExecutionHistory = async () => {
    if (!selectedWorkflow?._id) return;
    try {
      const automationServicePort = process.env.REACT_APP_AUTOMATION_SERVICE_PORT || 5001;
      const AUTOMATION_SERVICE_URL = process.env.REACT_APP_AUTOMATION_SERVICE_URL || `http://localhost:${automationServicePort}`;
      
      const response = await axios.get(
        `${AUTOMATION_SERVICE_URL}/api/workflows/${selectedWorkflow._id}/history?limit=50`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExecutionHistory(response.data.data || []);
    } catch (error) {
      console.error('Error fetching execution history:', error);
      toast.error('Failed to load execution history');
    }
  };

  const handleDeleteNode = (node) => {
    setConfirmModal({
      show: true,
      title: 'Delete Node',
      message: `Are you sure you want to delete this ${node.type} node? This will also remove all connections.`,
      onConfirm: () => {
        // Remove node and all connected edges
        const newNodes = nodes.filter(n => n.id !== node.id);
        const newEdges = edges.filter(e => e.source !== node.id && e.target !== node.id);
        setNodes(newNodes);
        setEdges(newEdges);
        setSelectedNode(null);
        setShowNodeConfig(false);
        toast.success('Node deleted successfully');
        setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
      },
      type: 'danger'
    });
  };

  // Keyboard shortcut for deleting selected node
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.key === 'Delete' || event.key === 'Backspace') && selectedNode && !showNodeConfig && !event.target.matches('input, textarea, select')) {
        event.preventDefault();
        handleDeleteNode(selectedNode);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedNode, showNodeConfig, nodes, edges]);

  // Validate workflow whenever nodes or edges change
  useEffect(() => {
    const validation = isWorkflowComplete();
    setWorkflowValidation(validation);
  }, [nodes, edges]);

  const fetchWorkflows = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/workflows`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkflows(response.data.data || []);
    } catch (error) {
      console.error('Error fetching workflows:', error);
      toast.error('Failed to fetch workflows');
    } finally {
      setLoading(false);
    }
  };

  const loadExampleWorkflow = async () => {
    try {
      const exampleWorkflow = {
        name: "Complete Feature Demo Workflow",
        description: "A comprehensive workflow demonstrating all node types and features available in the system",
        triggerType: "whatsapp",
        triggerConfig: { platform: "whatsapp" },
        llmConfig: {
          provider: "openrouter",
          useGlobalConfig: true,
          models: ["openai/gpt-4", "anthropic/claude-3-5-sonnet"],
          temperature: 0.7,
          maxTokens: 500,
          systemPrompt: "You are a helpful customer service assistant for an art and craft store. Help customers with product inquiries, pricing, orders, and provide friendly, professional responses."
        },
        responseTemplates: [
          {
            keyword: "greeting",
            response: "Hello! Welcome to our store. How can I help you today?",
            useLLM: false
          },
          {
            keyword: "price",
            response: "Let me check the pricing for you.",
            useLLM: true,
            llmPrompt: "The customer is asking about pricing. Provide helpful information about our products and pricing."
          }
        ],
        maxExecutionsPerHour: 100,
        nodes: [
          {
            id: "trigger-1",
            type: "trigger",
            position: { x: 100, y: 200 },
            data: {
              label: "WhatsApp Message Trigger",
              triggerType: "whatsapp",
              platform: "whatsapp"
            }
          },
          {
            id: "variable-1",
            type: "variable",
            position: { x: 300, y: 200 },
            data: {
              label: "Store API Base URL",
              variableName: "apiBaseUrl",
              variableValue: "https://api.example.com"
            }
          },
          {
            id: "http-1",
            type: "http",
            position: { x: 500, y: 200 },
            data: {
              label: "Fetch Customer Data",
              method: "GET",
              url: "{{$variables.apiBaseUrl}}/customers/{{$json.from}}",
              headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer {{$variables.apiToken}}"
              },
              body: "",
              timeout: 30000
            }
          },
          {
            id: "transform-1",
            type: "transform",
            position: { x: 700, y: 200 },
            data: {
              label: "Format Customer Data",
              input: "{{$node['http-1'].json.data}}",
              mapping: {
                customerId: "{{$json.id}}",
                customerName: "{{$json.firstName}} {{$json.lastName}}",
                customerEmail: "{{$json.email}}",
                customerPhone: "{{$json.phone}}",
                isVIP: "{{$json.membershipLevel === 'VIP'}}"
              }
            }
          },
          {
            id: "condition-1",
            type: "condition",
            position: { x: 900, y: 200 },
            data: {
              label: "Check if Greeting",
              conditionName: "Is Greeting",
              conditionExpression: "{{$json.message.toLowerCase().includes('hello') || $json.message.toLowerCase().includes('hi')}}",
              condition: "message contains 'hello' or message contains 'hi'",
              isActive: true
            }
          },
          {
            id: "llm-1",
            type: "llm",
            position: { x: 1100, y: 100 },
            data: {
              label: "Generate AI Response",
              model: "openai/gpt-4",
              prompt: "The customer said: {{$json.message}}. Customer name: {{$node['transform-1'].json.customerName}}. Generate a friendly, helpful response."
            }
          },
          {
            id: "loop-1",
            type: "loop",
            position: { x: 1300, y: 100 },
            data: {
              label: "Process Product Recommendations",
              loopType: "array",
              input: "{{$node['llm-1'].json.recommendations}}",
              continueOnError: true,
              maxIterations: 10
            }
          },
          {
            id: "delay-1",
            type: "delay",
            position: { x: 1500, y: 100 },
            data: {
              label: "Wait Before Sending",
              delay: 2
            }
          },
          {
            id: "action-1",
            type: "action",
            position: { x: 1700, y: 100 },
            data: {
              label: "Send WhatsApp Response",
              actionType: "send_whatsapp",
              message: "Hello {{$node['transform-1'].json.customerName}}! {{$node['llm-1'].json.response}}",
              platform: "whatsapp"
            }
          },
          {
            id: "http-2",
            type: "http",
            position: { x: 1100, y: 300 },
            data: {
              label: "Fetch Product Info",
              method: "POST",
              url: "{{$variables.apiBaseUrl}}/products/search",
              headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer {{$variables.apiToken}}"
              },
              body: "{\"query\": \"{{$json.message}}\", \"customerId\": \"{{$node['transform-1'].json.customerId}}\"}",
              timeout: 30000
            }
          },
          {
            id: "error-1",
            type: "error",
            position: { x: 1300, y: 300 },
            data: {
              label: "Handle API Errors",
              errorAction: "retry",
              maxRetries: 3,
              retryDelay: 1000
            }
          },
          {
            id: "action-2",
            type: "action",
            position: { x: 1500, y: 300 },
            data: {
              label: "Send Product Info",
              actionType: "send_whatsapp",
              message: "Here are the products matching your search: {{$node['http-2'].json.products}}",
              platform: "whatsapp"
            }
          }
        ],
        edges: [
          { id: "e1", source: "trigger-1", target: "variable-1", sourceHandle: null, targetHandle: null },
          { id: "e2", source: "variable-1", target: "http-1", sourceHandle: null, targetHandle: null },
          { id: "e3", source: "http-1", target: "transform-1", sourceHandle: null, targetHandle: null },
          { id: "e4", source: "transform-1", target: "condition-1", sourceHandle: null, targetHandle: null },
          { id: "e5", source: "condition-1", target: "llm-1", sourceHandle: "true", targetHandle: null },
          { id: "e6", source: "condition-1", target: "http-2", sourceHandle: "false", targetHandle: null },
          { id: "e7", source: "llm-1", target: "loop-1", sourceHandle: null, targetHandle: null },
          { id: "e8", source: "loop-1", target: "delay-1", sourceHandle: null, targetHandle: null },
          { id: "e9", source: "delay-1", target: "action-1", sourceHandle: null, targetHandle: null },
          { id: "e10", source: "http-2", target: "error-1", sourceHandle: null, targetHandle: null },
          { id: "e11", source: "error-1", target: "action-2", sourceHandle: null, targetHandle: null }
        ]
      };

      // Set workflow data
      setWorkflowName(exampleWorkflow.name);
      setWorkflowDescription(exampleWorkflow.description);
      setLlmConfig(exampleWorkflow.llmConfig);
      setResponseTemplates(exampleWorkflow.responseTemplates);

      // Convert to ReactFlow format
      const flowNodes = exampleWorkflow.nodes.map(node => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: { ...node.data, label: node.data?.label || node.type }
      }));

      const flowEdges = exampleWorkflow.edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        markerEnd: { type: MarkerType.ArrowClosed },
        deletable: true,
        selectable: true,
        style: { strokeWidth: 2, stroke: '#667eea' }
      }));

      setNodes(flowNodes);
      setEdges(flowEdges);
      setSelectedWorkflow(null);

      setTimeout(() => {
        if (reactFlowInstance) {
          reactFlowInstance.fitView({ padding: 0.2, duration: 300 });
        }
      }, 100);

      toast.success('Example workflow loaded! Configure API URLs and credentials before saving.');
    } catch (error) {
      console.error('Error loading example workflow:', error);
      toast.error('Failed to load example workflow');
    }
  };

  const loadWorkflow = async (workflow) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/workflows/${workflow._id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const workflowData = response.data.data;
      setWorkflowName(workflowData.name || '');
      setWorkflowDescription(workflowData.description || '');
      setLlmConfig(workflowData.llmConfig || {
        provider: 'openrouter',
        models: [],
        temperature: 0.7,
        maxTokens: 500,
        systemPrompt: 'You are a helpful customer service assistant. Respond professionally and helpfully to customer inquiries.',
        useGlobalConfig: true
      });
      setResponseTemplates(workflowData.responseTemplates || []);

      // Convert workflow nodes/edges to ReactFlow format
      const flowNodes = (workflowData.nodes || []).map(node => ({
        id: node.id,
        type: node.type,
        position: node.position || { x: 0, y: 0 },
        data: { ...node.data, label: node.data?.label || node.label || node.type }
      }));

      const flowEdges = (workflowData.edges || []).map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        sourceHandle: edge.sourceHandle,
        targetHandle: edge.targetHandle,
        markerEnd: { type: MarkerType.ArrowClosed },
        deletable: true,
        selectable: true,
        style: { strokeWidth: 2, stroke: '#667eea' }
      }));

      // Use setTimeout to ensure ReactFlow is ready before setting nodes/edges
      setTimeout(() => {
        setNodes(flowNodes);
        setEdges(flowEdges);
        
        // Fit view after a short delay to ensure nodes are rendered
        setTimeout(() => {
          if (reactFlowInstance) {
            reactFlowInstance.fitView({ padding: 0.2, duration: 300 });
          }
        }, 100);
      }, 50);
      
      toast.success('Workflow loaded successfully');
    } catch (error) {
      console.error('Error loading workflow:', error);
      toast.error('Failed to load workflow: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const onConnect = useCallback(
    (params) => {
      const newEdge = {
        ...params,
        deletable: true,
        selectable: true,
        style: { strokeWidth: 2, stroke: '#667eea' },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#667eea' }
      };
      setEdges((eds) => addEdge(newEdge, eds));
    },
    [setEdges]
  );

  const onEdgeUpdate = useCallback(
    (oldEdge, newConnection) => {
      setEdges((els) => updateEdge(oldEdge, newConnection, els));
    },
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      if (!type) return;

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY
      });

      const newNode = {
        id: `${type}-${Date.now()}`,
        type,
        position,
        data: {
          label: type.charAt(0).toUpperCase() + type.slice(1),
          ...(type === 'trigger' && { triggerType: 'whatsapp' }),
          ...(type === 'llm' && { model: '', prompt: '' }),
          ...(type === 'action' && { actionType: 'send_message' }),
          ...(type === 'condition' && { condition: 'if/else', conditionName: 'Condition 1', isActive: true }),
          ...(type === 'delay' && { delay: 5 }),
          ...(type === 'http' && { method: 'GET', url: '', headers: {}, body: '', timeout: 30000 }),
          ...(type === 'loop' && { loopType: 'array', input: '', continueOnError: false, maxIterations: null }),
          ...(type === 'transform' && { input: '', mapping: {} }),
          ...(type === 'variable' && { variableName: '', variableValue: '' }),
          ...(type === 'error' && { errorAction: 'throw', maxRetries: 3, retryDelay: 1000 })
        }
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [reactFlowInstance, setNodes]
  );

  const handleSave = async () => {
    if (!workflowName.trim()) {
      toast.error('Please enter a workflow name');
      return;
    }

    setSaving(true);
    try {
      const workflowData = {
        name: workflowName,
        description: workflowDescription,
        nodes: nodes.map(node => ({
          id: node.id,
          type: node.type,
          position: node.position,
          data: node.data,
          label: node.data.label || node.type
        })),
        edges: edges.map(edge => ({
          id: edge.id,
          source: edge.source,
          target: edge.target,
          sourceHandle: edge.sourceHandle,
          targetHandle: edge.targetHandle
        })),
        triggerType: nodes.find(n => n.type === 'trigger')?.data?.triggerType || 'manual',
        triggerConfig: nodes.find(n => n.type === 'trigger')?.data || {},
        llmConfig,
        responseTemplates
      };

      if (selectedWorkflow?._id) {
        await axios.put(
          `${API_URL}/api/workflows/${selectedWorkflow._id}`,
          workflowData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Workflow updated successfully!');
      } else {
        const response = await axios.post(
          `${API_URL}/api/workflows`,
          workflowData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSelectedWorkflow(response.data.data);
        toast.success('Workflow created successfully!');
      }

      fetchWorkflows();
    } catch (error) {
      console.error('Error saving workflow:', error);
      toast.error(error.response?.data?.message || 'Failed to save workflow');
    } finally {
      setSaving(false);
    }
  };

  const handleNewWorkflow = () => {
    setSelectedWorkflow(null);
    setWorkflowName('');
    setWorkflowDescription('');
    setNodes([]);
    setEdges([]);
    setLlmConfig({
      provider: 'openrouter',
      models: [],
      temperature: 0.7,
      maxTokens: 500,
      systemPrompt: 'You are a helpful customer service assistant. Respond professionally and helpfully to customer inquiries.',
      useGlobalConfig: true
    });
    setResponseTemplates([]);
  };

  const handleDelete = (workflow) => {
    setConfirmModal({
      show: true,
      title: 'Delete Workflow',
      message: `Are you sure you want to delete "${workflow.name}"? This action cannot be undone.`,
      onConfirm: async () => {
        try {
          await axios.delete(`${API_URL}/api/workflows/${workflow._id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          toast.success('Workflow deleted successfully');
          if (selectedWorkflow?._id === workflow._id) {
            handleNewWorkflow();
          }
          fetchWorkflows();
        } catch (error) {
          toast.error('Failed to delete workflow');
        } finally {
          setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' });
        }
      },
      type: 'danger'
    });
  };

  const addResponseTemplate = () => {
    if (!newTemplate.keyword || !newTemplate.response) {
      toast.error('Please fill in keyword and response');
      return;
    }
    setResponseTemplates([...responseTemplates, { ...newTemplate }]);
    setNewTemplate({ keyword: '', response: '', useLLM: false, llmPrompt: '' });
    toast.success('Response template added');
  };

  const removeResponseTemplate = (index) => {
    setResponseTemplates(responseTemplates.filter((_, i) => i !== index));
  };

  // Suppress ResizeObserver errors (common React issue, not critical)
  useEffect(() => {
    const originalError = console.error;
    const originalWarn = console.warn;
    
    // Suppress ResizeObserver errors
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop completed with undelivered notifications')) {
        return; // Suppress this specific error
      }
      originalError.apply(console, args);
    };
    
    console.warn = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return; // Suppress ResizeObserver warnings
      }
      originalWarn.apply(console, args);
    };
    
    // Also catch unhandled errors
    const handleError = (e) => {
      if (e.message && e.message.includes('ResizeObserver')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };
    
    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', (e) => {
      if (e.reason && e.reason.message && e.reason.message.includes('ResizeObserver')) {
        e.preventDefault();
      }
    });
    
    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener('error', handleError);
    };
  }, []);

  const validateNodeData = (node) => {
    const errors = [];
    
    if (node.type === 'trigger') {
      if (!node.data.triggerType && !node.data.platform) {
        errors.push(`Trigger node "${node.data.label || node.id}" is missing trigger type/platform`);
      }
      if ((node.data.triggerType === 'whatsapp' || node.data.platform === 'whatsapp') && !aiConfig?.whatsapp?.accessToken) {
        errors.push(`WhatsApp trigger requires WhatsApp configuration in AI Configuration Manager`);
      }
      if ((node.data.triggerType === 'facebook' || node.data.platform === 'facebook') && !aiConfig?.meta?.accessToken) {
        errors.push(`Facebook trigger requires Meta configuration in AI Configuration Manager`);
      }
      if ((node.data.triggerType === 'instagram' || node.data.platform === 'instagram') && !aiConfig?.meta?.accessToken) {
        errors.push(`Instagram trigger requires Meta configuration in AI Configuration Manager`);
      }
      if ((node.data.triggerType === 'google' || node.data.platform === 'google') && !aiConfig?.google?.refreshToken) {
        errors.push(`Google trigger requires Google configuration in AI Configuration Manager`);
      }
    }
    
    if (node.type === 'llm') {
      if (!llmConfig.systemPrompt && !node.data.prompt) {
        errors.push(`LLM node "${node.data.label || node.id}" is missing system prompt or node-specific prompt`);
      }
      if (!aiConfig?.openrouter?.apiKey) {
        errors.push(`LLM node requires OpenRouter API key configuration in AI Configuration Manager`);
      }
    }
    
    if (node.type === 'action') {
      if (!node.data.actionType) {
        errors.push(`Action node "${node.data.label || node.id}" is missing action type`);
      }
      if ((node.data.actionType === 'send_message' || node.data.actionType === 'send_whatsapp') && !node.data.message && !node.data.messageTemplate) {
        errors.push(`Action node "${node.data.label || node.id}" (${node.data.actionType}) is missing message content`);
      }
      if ((node.data.actionType === 'send_facebook_post' || node.data.actionType === 'send_instagram_post') && !node.data.postContent && !node.data.message) {
        errors.push(`Action node "${node.data.label || node.id}" (${node.data.actionType}) is missing post content`);
      }
      if (node.data.platform) {
        if (node.data.platform === 'whatsapp' && !aiConfig?.whatsapp?.accessToken) {
          errors.push(`WhatsApp action requires WhatsApp configuration in AI Configuration Manager`);
        }
        if ((node.data.platform === 'facebook' || node.data.platform === 'instagram') && !aiConfig?.meta?.accessToken) {
          errors.push(`${node.data.platform} action requires Meta configuration in AI Configuration Manager`);
        }
        if (node.data.platform === 'google' && !aiConfig?.google?.refreshToken) {
          errors.push(`Google action requires Google configuration in AI Configuration Manager`);
        }
      }
    }
    
    if (node.type === 'condition') {
      if (!node.data.condition) {
        errors.push(`Condition node "${node.data.label || node.id}" is missing condition logic`);
      }
      if (!node.data.conditionName) {
        errors.push(`Condition node "${node.data.label || node.id}" is missing condition name`);
      }
    }
    
    if (node.type === 'delay') {
      if (!node.data.delay || node.data.delay <= 0) {
        errors.push(`Delay node "${node.data.label || node.id}" is missing or has invalid delay time`);
      }
    }
    
    return errors;
  };

  // Validate workflow completeness
  const isWorkflowComplete = () => {
    const validationErrors = [];
    const nodeErrorsMap = new Map();
    
    // Check if there's at least one node
    if (nodes.length === 0) {
      setNodeErrors(new Map());
      return { valid: false, message: 'Workflow must have at least one node', errors: ['Workflow must have at least one node'], nodeErrors: new Map() };
    }

    // Check if there's a trigger node
    const hasTrigger = nodes.some(node => node.type === 'trigger');
    if (!hasTrigger) {
      setNodeErrors(new Map());
      return { valid: false, message: 'Workflow must have a Trigger node', errors: ['Workflow must have a Trigger node'], nodeErrors: new Map() };
    }

    // Validate each node's data
    nodes.forEach(node => {
      const nodeErrorList = validateNodeData(node);
      if (nodeErrorList.length > 0) {
        nodeErrorsMap.set(node.id, nodeErrorList);
        validationErrors.push(...nodeErrorList);
      }
    });
    
    // Update node errors state
    setNodeErrors(nodeErrorsMap);

    // Check if all nodes are connected (except isolated nodes)
    const triggerNodes = nodes.filter(node => node.type === 'trigger');
    const hasConnections = edges.length > 0;
    
    if (!hasConnections && nodes.length > 1) {
      validationErrors.push('All nodes must be connected');
    }

    // Check if trigger has outgoing connections
    const triggerHasOutgoing = triggerNodes.some(trigger => 
      edges.some(edge => edge.source === trigger.id)
    );
    
    if (!triggerHasOutgoing && nodes.length > 1) {
      validationErrors.push('Trigger node must be connected to other nodes');
    }

    // Check if there's at least one action or LLM node (something that produces output)
    const hasOutputNode = nodes.some(node => 
      node.type === 'action' || node.type === 'llm'
    );
    
    if (!hasOutputNode && nodes.length > 1) {
      validationErrors.push('Workflow must have at least one Action or AI Response node');
    }

    // Check if there's a path from trigger to an output node
    const triggerIds = triggerNodes.map(n => n.id);
    const outputNodeIds = nodes
      .filter(n => n.type === 'action' || n.type === 'llm')
      .map(n => n.id);
    
    if (outputNodeIds.length > 0) {
      // BFS to check if any trigger can reach any output node
      const canReachOutput = (startNodeId) => {
        const visited = new Set();
        const queue = [startNodeId];
        visited.add(startNodeId);

        while (queue.length > 0) {
          const currentId = queue.shift();
          
          // Check if current node is an output node
          if (outputNodeIds.includes(currentId)) {
            return true;
          }

          // Add all nodes reachable from current node
          edges
            .filter(edge => edge.source === currentId)
            .forEach(edge => {
              if (!visited.has(edge.target)) {
                visited.add(edge.target);
                queue.push(edge.target);
              }
            });
        }
        return false;
      };

      const hasPath = triggerIds.some(triggerId => canReachOutput(triggerId));
      if (!hasPath) {
        validationErrors.push('Trigger must be connected to an Action or AI Response node');
      }
    }

    if (validationErrors.length > 0) {
      return { 
        valid: false, 
        message: validationErrors[0], 
        errors: validationErrors
      };
    }

    return { valid: true, message: 'Workflow is complete', errors: [] };
  };

  return (
    <div className="workflow-manager">
      <div className="workflow-header">
        <h2>🤖 AI Workflow Orchestrator</h2>
        <p className="subtitle">Build automated workflows with drag-and-drop and AI-powered responses</p>
      </div>

      <div className="workflow-layout">
        {/* Sidebar - Workflow List */}
        <div className="workflow-sidebar">
          <div className="sidebar-header">
            <h3>Workflows</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <button onClick={handleNewWorkflow} className="btn btn-primary btn-sm">
                <FaPlus /> New
              </button>
              <button 
                onClick={loadExampleWorkflow}
                className="btn btn-secondary btn-sm" 
                title="Load a complete example workflow with all node types and features"
                style={{ fontSize: '12px' }}
              >
                <FaDatabase /> Load Example
              </button>
            </div>
          </div>
          <div className="workflow-list">
            {loading ? (
              <div className="loading">Loading workflows...</div>
            ) : workflows.length === 0 ? (
              <div className="empty-state">No workflows yet. Create one to get started!</div>
            ) : (
              workflows.map((workflow) => (
                <div
                  key={workflow._id}
                  className={`workflow-item ${selectedWorkflow?._id === workflow._id ? 'active' : ''}`}
                  onClick={() => setSelectedWorkflow(workflow)}
                >
                  <div className="workflow-item-header">
                    <span className="workflow-name">{workflow.name}</span>
                    <span className={`workflow-status ${workflow.isActive ? 'active' : 'inactive'}`}>
                      {workflow.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="workflow-item-actions">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(workflow);
                      }}
                      className="btn-icon btn-danger"
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main Canvas */}
        <div className="workflow-canvas-container">
          <div className="workflow-toolbar">
            <div className="toolbar-left">
              <input
                type="text"
                placeholder="Workflow Name"
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                className="workflow-name-input"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={workflowDescription}
                onChange={(e) => setWorkflowDescription(e.target.value)}
                className="workflow-desc-input"
              />
            </div>
            <div className="toolbar-right">
              {selectedWorkflow?._id && (
                <button 
                  onClick={() => setShowChat(true)} 
                  className="btn btn-secondary"
                  title="View Conversations"
                >
                  <FaComments /> Conversations
                </button>
              )}
              <button onClick={() => setShowLLMConfig(!showLLMConfig)} className="btn btn-secondary">
                <FaRobot /> LLM Config
              </button>
              <button onClick={handleSave} className="btn btn-primary" disabled={saving}>
                <FaSave /> {saving ? 'Saving...' : 'Save Workflow'}
              </button>
              <button 
                onClick={async () => {
                  // Comprehensive validation before execution
                  const validation = isWorkflowComplete();
                  if (!validation.valid) {
                    // Show validation modal with all errors
                    setShowValidationModal(true);
                    return;
                  }

                  if (!selectedWorkflow?._id) {
                    toast.error('Please save the workflow first');
                    return;
                  }
                  
                  try {
                    setWorkflowExecutionStatus('executing');
                    setFinalResponse(null);
                    
                    // Prepare workflow data with all node configurations
                    const workflowData = {
                      nodes: nodes.map(node => ({
                        id: node.id,
                        type: node.type,
                        data: node.data,
                        position: node.position
                      })),
                      edges: edges,
                      llmConfig: llmConfig,
                      triggerData: { 
                        message: 'Test message', 
                        user: 'Test User',
                        platform: nodes.find(n => n.type === 'trigger')?.data?.platform || nodes.find(n => n.type === 'trigger')?.data?.triggerType || 'manual'
                      }
                    };
                    
                    const response = await axios.post(
                      `${API_URL}/api/workflows/${selectedWorkflow._id}/execute`,
                      workflowData,
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    
                    // Process node outputs from execution steps
                    const outputsMap = new Map();
                    if (response.data?.result?.steps) {
                      response.data.result.steps.forEach(step => {
                        if (step.nodeId && step.result) {
                          outputsMap.set(step.nodeId, {
                            success: step.result.success !== false,
                            data: step.result.json || step.result.data || step.result,
                            message: step.result.message,
                            error: step.result.error,
                            nodeType: step.nodeType
                          });
                        }
                      });
                    }
                    setNodeOutputs(outputsMap);
                    
                    setFinalResponse(response.data);
                    setWorkflowExecutionStatus('completed');
                    toast.success('Workflow executed successfully!');
                  } catch (error) {
                    console.error('Workflow execution error:', error);
                    const errorMessage = error.response?.data?.message || error.response?.data?.error || 'Execution failed';
                    setFinalResponse({ error: errorMessage });
                    setWorkflowExecutionStatus('failed');
                    toast.error(`Workflow execution failed: ${errorMessage}`);
                  }
                }}
                className="btn btn-success"
                disabled={!selectedWorkflow?._id || workflowExecutionStatus === 'executing'}
                title={workflowValidation.valid 
                  ? "Execute workflow and see final response" 
                  : "Click to see validation errors"}
              >
                <FaPlay /> {workflowExecutionStatus === 'executing' ? 'Executing...' : 'Execute & Test'}
                {!workflowValidation.valid && <FaInfoCircle style={{ marginLeft: '8px' }} />}
              </button>
            </div>
          </div>

          <div className="workflow-canvas-wrapper" ref={reactFlowWrapper}>
            <ReactFlow
              nodes={nodes.map(node => ({
                ...node,
                selected: selectedNode?.id === node.id,
                data: {
                  ...node.data,
                  hasError: nodeErrors.has(node.id)
                }
              }))}
              edges={edges.map(edge => ({
                ...edge,
                deletable: true,
                selectable: true,
                style: { strokeWidth: 2, stroke: '#667eea' }
              }))}
              onNodesChange={onNodesChange}
              onEdgesChange={onEdgesChange}
              onConnect={onConnect}
              onInit={setReactFlowInstance}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onNodeClick={(event, node) => {
                // Check if clicking on output indicator
                const target = event.target;
                if (target.classList?.contains('node-output-indicator') || target.closest?.('.node-output-indicator')) {
                  setShowNodeOutput(node.id);
                  return;
                }
                setSelectedNode(node);
                setShowNodeConfig(true);
              }}
              onPaneClick={() => {
                setSelectedNode(null);
                setShowNodeConfig(false);
              }}
              onNodesDelete={(deleted) => {
                // Remove edges connected to deleted nodes
                const deletedIds = deleted.map(n => n.id);
                const newEdges = edges.filter(e => 
                  !deletedIds.includes(e.source) && !deletedIds.includes(e.target)
                );
                setEdges(newEdges);
                if (selectedNode && deletedIds.includes(selectedNode.id)) {
                  setSelectedNode(null);
                  setShowNodeConfig(false);
                }
                toast.success('Node(s) deleted successfully');
              }}
              onEdgesDelete={(deleted) => {
                // Check if this deletion was already confirmed
                if (deleted.length > 0) {
                  const deletedEdge = deleted[0];
                  if (confirmedDeletions.has(deletedEdge.id)) {
                    // This was a confirmed deletion, allow it
                    setConfirmedDeletions(prev => {
                      const newSet = new Set(prev);
                      newSet.delete(deletedEdge.id);
                      return newSet;
                    });
                    toast.success('Connection deleted successfully');
                  } else {
                    // This was not confirmed, restore and show confirmation
                    setPendingEdgeDeletion(deletedEdge);
                    setSelectedEdge(deletedEdge);
                    setShowEdgeDeleteConfirm(true);
                    // Restore the edge temporarily until user confirms
                    setEdges((eds) => [...eds, deletedEdge]);
                  }
                }
              }}
              onEdgeClick={(event, edge) => {
                setSelectedEdge(edge);
                event.stopPropagation();
              }}
              onEdgeContextMenu={(event, edge) => {
                event.preventDefault();
                setSelectedEdge(edge);
                setShowEdgeDeleteConfirm(true);
              }}
              edgesFocusable={true}
              edgesUpdatable={true}
              onEdgeUpdate={onEdgeUpdate}
              connectionMode="loose"
              nodeTypes={nodeTypes}
              connectionLineStyle={{ stroke: '#667eea', strokeWidth: 2 }}
              snapToGrid={true}
              snapGrid={[15, 15]}
              defaultEdgeOptions={{ 
                type: 'smoothstep',
                animated: true,
                markerEnd: { type: MarkerType.ArrowClosed, color: '#667eea' },
                style: { strokeWidth: 2, stroke: '#667eea' },
                deletable: true,
                selectable: true
              }}
              deleteKeyCode="Delete"
              multiSelectionKeyCode="Shift"
              fitView={false}
            >
              <Background />
              <Controls />
              <MiniMap />
              <Panel position="top-left" className="node-panel">
                {showNodePanel && (
                  <div className="node-palette">
                    <h4>Drag & Drop Nodes</h4>
                    <div className="palette-items">
                      <div
                        className="palette-item"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('application/reactflow', 'trigger')}
                        title="Trigger: Starts the workflow. Can be WhatsApp, Instagram, Facebook, Google, Webhook, or Manual."
                      >
                        <FaBolt /> Trigger
                        <small>Starts workflow</small>
                      </div>
                      <div
                        className="palette-item"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('application/reactflow', 'llm')}
                        title="AI Response: Uses LLM (GPT) to generate intelligent responses based on input."
                      >
                        <FaRobot /> AI Response
                        <small>LLM processing</small>
                      </div>
                      <div
                        className="palette-item"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('application/reactflow', 'action')}
                        title="Action: Performs actions like sending messages, posting to social media, or creating ads."
                      >
                        <FaComment /> Action
                        <small>Execute action</small>
                      </div>
                      <div
                        className="palette-item"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('application/reactflow', 'condition')}
                        title="Condition: Branching logic - routes workflow based on conditions (if/else)."
                      >
                        <FaCodeBranch /> Condition
                        <small>Branch logic</small>
                      </div>
                      <div
                        className="palette-item"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('application/reactflow', 'delay')}
                        title="Delay: Adds a time delay before proceeding to the next step."
                      >
                        <FaClock /> Delay
                        <small>Wait time</small>
                      </div>
                      <div
                        className="palette-item"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('application/reactflow', 'http')}
                        title="HTTP Request: Make HTTP requests to external APIs."
                      >
                        <FaNetworkWired /> HTTP Request
                        <small>API calls</small>
                      </div>
                      <div
                        className="palette-item"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('application/reactflow', 'loop')}
                        title="Loop: Iterate over arrays, objects, or while conditions."
                      >
                        <FaSync /> Loop
                        <small>Iterate data</small>
                      </div>
                      <div
                        className="palette-item"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('application/reactflow', 'transform')}
                        title="Transform: Transform and map data structures."
                      >
                        <FaExchangeAlt /> Transform
                        <small>Data mapping</small>
                      </div>
                      <div
                        className="palette-item"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('application/reactflow', 'variable')}
                        title="Variable: Set variables for use in expressions."
                      >
                        <FaTag /> Variable
                        <small>Set variable</small>
                      </div>
                      <div
                        className="palette-item"
                        draggable
                        onDragStart={(e) => e.dataTransfer.setData('application/reactflow', 'error')}
                        title="Error Handler: Handle errors with retry logic or continue on error."
                      >
                        <FaExclamationTriangle /> Error Handler
                        <small>Error handling</small>
                      </div>
                    </div>
                  </div>
                )}
              </Panel>
            </ReactFlow>
          </div>

          {/* LLM Configuration Panel */}
          {showLLMConfig && (
            <div className="llm-config-panel">
              <h3>LLM Configuration (OpenRouter)</h3>
              
              <div className="form-group">
                <label>Configuration Source</label>
                <div style={{ padding: '10px', background: '#d4edda', borderRadius: '4px', color: '#155724', marginBottom: '10px' }}>
                  ✓ Using global OpenRouter configuration from AI Configuration Manager
                </div>
                <small>All API credentials are managed in <a href="/ai-config" target="_blank">AI Configuration Manager → OpenRouter (LLM)</a></small>
              </div>
              
              {aiConfig?.openrouter ? (
                <>
                  <div className="form-group">
                    <label>API Key Status</label>
                    <div style={{ padding: '10px', background: aiConfig.openrouter.apiKey ? '#d4edda' : '#f8d7da', borderRadius: '4px', color: aiConfig.openrouter.apiKey ? '#155724' : '#721c24' }}>
                      {aiConfig.openrouter.apiKey ? '✓ API Key Configured' : '✗ API Key Not Configured'}
                    </div>
                    <small>Configure in <a href="/ai-config" target="_blank">AI Configuration Manager</a></small>
                  </div>
                  <div className="form-group">
                    <label>Base URL</label>
                    <input
                      type="text"
                      value={aiConfig.openrouter.baseUrl || 'https://openrouter.ai/api/v1'}
                      disabled
                      style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    <small>Configured in AI Configuration Manager</small>
                  </div>
                  <div className="form-group">
                    <label>Default Model</label>
                    <input
                      type="text"
                      value={aiConfig.openrouter.defaultModel || 'openai/gpt-3.5-turbo'}
                      disabled
                      style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                    />
                    <small>Configured in AI Configuration Manager</small>
                  </div>
                  {aiConfig.openrouter.models && aiConfig.openrouter.models.length > 0 && (
                    <div className="form-group">
                      <label>Global Additional Models</label>
                      <div style={{ padding: '10px', background: '#f5f5f5', borderRadius: '4px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {aiConfig.openrouter.models.map((model, index) => (
                            <span
                              key={index}
                              style={{
                                padding: '4px 8px',
                                background: '#e7f3ff',
                                border: '1px solid #b3d9ff',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}
                            >
                              {model}
                            </span>
                          ))}
                        </div>
                      </div>
                      <small>These models are available globally from AI Configuration Manager</small>
                    </div>
                  )}
                  {aiConfig.openrouter.organizationId && (
                    <div className="form-group">
                      <label>Organization ID</label>
                      <input
                        type="text"
                        value={aiConfig.openrouter.organizationId}
                        disabled
                        style={{ background: '#f5f5f5', cursor: 'not-allowed' }}
                      />
                      <small>Configured in AI Configuration Manager</small>
                    </div>
                  )}
                </>
              ) : (
                <div className="form-group">
                  <div style={{ padding: '10px', background: '#fff3cd', borderRadius: '4px', color: '#856404' }}>
                    ⚠️ OpenRouter not configured. Please configure it in <a href="/ai-config" target="_blank">AI Configuration Manager</a>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Additional Models (Optional)</label>
                <small style={{ display: 'block', marginBottom: '10px', color: '#666' }}>
                  Default model: <strong>{aiConfig?.openrouter?.defaultModel || 'openai/gpt-3.5-turbo'}</strong> (from AI Configuration)
                  <br />
                  Select additional models to use in this workflow. The default model will always be available.
                </small>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
                  {llmConfig.models && llmConfig.models.length > 0 && llmConfig.models.map((model, index) => (
                    <span
                      key={index}
                      style={{
                        padding: '6px 12px',
                        background: '#e7f3ff',
                        border: '1px solid #b3d9ff',
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      {model}
                      <button
                        type="button"
                        onClick={() => {
                          const newModels = llmConfig.models.filter((_, i) => i !== index);
                          setLlmConfig({ ...llmConfig, models: newModels });
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc3545',
                          cursor: 'pointer',
                          fontSize: '14px',
                          padding: '0',
                          lineHeight: '1'
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value && !llmConfig.models?.includes(e.target.value)) {
                      setLlmConfig({
                        ...llmConfig,
                        models: [...(llmConfig.models || []), e.target.value]
                      });
                    }
                    e.target.value = '';
                  }}
                  style={{ width: '100%', padding: '8px', marginBottom: '8px' }}
                >
                  <option value="">Select a model to add...</option>
                  {aiConfig?.openrouter?.models && aiConfig.openrouter.models.length > 0 && (
                    <optgroup label="Global Models (from AI Configuration)">
                      {aiConfig.openrouter.models.map((model) => (
                        <option key={model} value={model} disabled={llmConfig.models?.includes(model)}>
                          {model} {llmConfig.models?.includes(model) ? '(already added)' : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  <optgroup label="OpenAI Models">
                    <option value="openai/gpt-4">GPT-4</option>
                    <option value="openai/gpt-4-turbo">GPT-4 Turbo</option>
                    <option value="openai/gpt-3.5-turbo">GPT-3.5 Turbo</option>
                    <option value="openai/gpt-4o">GPT-4o</option>
                    <option value="openai/gpt-4o-mini">GPT-4o Mini</option>
                  </optgroup>
                  <optgroup label="Anthropic Models">
                    <option value="anthropic/claude-3-opus">Claude 3 Opus</option>
                    <option value="anthropic/claude-3-sonnet">Claude 3 Sonnet</option>
                    <option value="anthropic/claude-3-haiku">Claude 3 Haiku</option>
                    <option value="anthropic/claude-3-5-sonnet">Claude 3.5 Sonnet</option>
                  </optgroup>
                  <optgroup label="Google Models">
                    <option value="google/gemini-pro">Gemini Pro</option>
                    <option value="google/gemini-pro-1.5">Gemini Pro 1.5</option>
                    <option value="google/gemini-flash-1.5">Gemini Flash 1.5</option>
                  </optgroup>
                  <optgroup label="Other Models">
                    <option value="meta-llama/llama-3-70b-instruct">Llama 3 70B</option>
                    <option value="mistralai/mistral-large">Mistral Large</option>
                    <option value="perplexity/llama-3-sonar-large-32k-online">Perplexity Sonar Large</option>
                  </optgroup>
                </select>
                <input
                  type="text"
                  placeholder="Or enter custom model (e.g., custom/model-name)"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && e.target.value.trim()) {
                      const customModel = e.target.value.trim();
                      if (!llmConfig.models?.includes(customModel)) {
                        setLlmConfig({
                          ...llmConfig,
                          models: [...(llmConfig.models || []), customModel]
                        });
                        e.target.value = '';
                      }
                    }
                  }}
                  style={{ width: '100%', padding: '8px' }}
                />
                <small>Add multiple models to use in this workflow. The default model from AI Configuration will always be available.</small>
              </div>
              
              <div className="form-group">
                <label>Temperature</label>
                <input
                  type="number"
                  min="0"
                  max="2"
                  step="0.1"
                  value={llmConfig.temperature}
                  onChange={(e) => setLlmConfig({ ...llmConfig, temperature: parseFloat(e.target.value) || 0.7 })}
                />
                <small>Controls randomness (0.0 = deterministic, 2.0 = very creative). Default: 0.7</small>
              </div>
              
              <div className="form-group">
                <label>Max Tokens</label>
                <input
                  type="number"
                  min="1"
                  max="4000"
                  value={llmConfig.maxTokens}
                  onChange={(e) => setLlmConfig({ ...llmConfig, maxTokens: parseInt(e.target.value) || 500 })}
                />
                <small>Maximum number of tokens in the response. Default: 500</small>
              </div>
              
              <div className="form-group">
                <label>System Prompt</label>
                <textarea
                  value={llmConfig.systemPrompt}
                  onChange={(e) => setLlmConfig({ ...llmConfig, systemPrompt: e.target.value })}
                  rows="4"
                  placeholder="System prompt for AI responses"
                />
                <small>Define the AI's role and behavior for this workflow</small>
              </div>

              <h4 style={{ marginTop: '20px' }}>Response Templates</h4>
              <div className="response-templates">
                {responseTemplates.map((template, index) => (
                  <div key={index} className="template-item">
                    <strong>{template.keyword}</strong>: {template.response}
                    <button onClick={() => removeResponseTemplate(index)} className="btn-icon btn-danger">
                      <FaTrash />
                    </button>
                  </div>
                ))}
                <div className="template-form">
                  <input
                    type="text"
                    placeholder="Keyword"
                    value={newTemplate.keyword}
                    onChange={(e) => setNewTemplate({ ...newTemplate, keyword: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Response"
                    value={newTemplate.response}
                    onChange={(e) => setNewTemplate({ ...newTemplate, response: e.target.value })}
                  />
                  <label>
                    <input
                      type="checkbox"
                      checked={newTemplate.useLLM}
                      onChange={(e) => setNewTemplate({ ...newTemplate, useLLM: e.target.checked })}
                    />
                    Use LLM
                  </label>
                  <button onClick={addResponseTemplate} className="btn btn-primary btn-sm">
                    Add
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Expression Helper Panel */}
      {expressionHelper.show && (
        <div className="modal-overlay" onClick={() => setExpressionHelper({ show: false, fieldRef: null, fieldType: null })}>
          <div className="expression-helper-panel" onClick={(e) => e.stopPropagation()}>
            <div className="expression-helper-header">
              <h4>Insert Expression</h4>
              <button className="btn-icon" onClick={() => setExpressionHelper({ show: false, fieldRef: null, fieldType: null })}>
                <FaTimes />
              </button>
            </div>
            <div className="expression-helper-body">
              <div className="expression-category">
                <strong>Previous Nodes:</strong>
                <div className="expression-list">
                  {nodes
                    .filter(n => n.id !== selectedNode?.id && n.type !== 'trigger')
                    .map(node => (
                      <button
                        key={node.id}
                        className="expression-item"
                        onClick={() => {
                          const expression = `{{$node["${node.data?.label || node.id}"].json}}`;
                          if (expressionHelper.fieldRef) {
                            const event = { target: { value: expression } };
                            expressionHelper.fieldRef.onChange(event);
                          }
                          setExpressionHelper({ show: false, fieldRef: null, fieldType: null });
                        }}
                        title={`Insert: {{$node["${node.data?.label || node.id}"].json}}`}
                      >
                        <FaCodeBranch /> {node.data?.label || node.id}
                        <small>Click to insert</small>
                      </button>
                    ))}
                  {nodes.filter(n => n.id !== selectedNode?.id && n.type !== 'trigger').length === 0 && (
                    <div className="expression-empty">No previous nodes available</div>
                  )}
                </div>
              </div>
              <div className="expression-category">
                <strong>Common Expressions:</strong>
                <div className="expression-list">
                  <button
                    className="expression-item"
                    onClick={() => {
                      const expression = `{{$json}}`;
                      if (expressionHelper.fieldRef) {
                        const event = { target: { value: expression } };
                        expressionHelper.fieldRef.onChange(event);
                      }
                      setExpressionHelper({ show: false, fieldRef: null, fieldType: null });
                    }}
                  >
                    <FaTag /> Previous Node Data
                    <small>{'{{$json}}'}</small>
                  </button>
                  <button
                    className="expression-item"
                    onClick={() => {
                      const expression = `{{$json.message}}`;
                      if (expressionHelper.fieldRef) {
                        const event = { target: { value: expression } };
                        expressionHelper.fieldRef.onChange(event);
                      }
                      setExpressionHelper({ show: false, fieldRef: null, fieldType: null });
                    }}
                  >
                    <FaTag /> Message Field
                    <small>{'{{$json.message}}'}</small>
                  </button>
                  <button
                    className="expression-item"
                    onClick={() => {
                      const expression = `{{$variables}}`;
                      if (expressionHelper.fieldRef) {
                        const event = { target: { value: expression } };
                        expressionHelper.fieldRef.onChange(event);
                      }
                      setExpressionHelper({ show: false, fieldRef: null, fieldType: null });
                    }}
                  >
                    <FaTag /> All Variables
                    <small>{'{{$variables}}'}</small>
                  </button>
                </div>
              </div>
              <div className="expression-category">
                <strong>Examples:</strong>
                <div className="expression-examples">
                  <code>{'{{$node["NodeName"].json.field}}'}</code>
                  <code>{'{{$json.field}}'}</code>
                  <code>{'{{$variables.varName}}'}</code>
                  <code>{'{{$json.firstName}} {{$json.lastName}}'}</code>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Node Configuration Modal */}
      {showNodeConfig && selectedNode && (
        <div className="node-config-modal-overlay" onClick={() => setShowNodeConfig(false)}>
          <div className="node-config-modal" onClick={(e) => e.stopPropagation()}>
            <div className="node-config-header">
              <div>
                <h3>Configure {selectedNode.type.charAt(0).toUpperCase() + selectedNode.type.slice(1)} Node</h3>
                <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#666', fontWeight: 'normal' }}>
                  {selectedNode.type === 'trigger' && 'Starts the workflow when a specific event occurs (e.g., incoming message, webhook, schedule)'}
                  {selectedNode.type === 'llm' && 'Uses AI to generate intelligent responses based on the input message and context'}
                  {selectedNode.type === 'action' && 'Performs an action like sending a message, posting to social media, or creating ads'}
                  {selectedNode.type === 'condition' && 'Routes the workflow based on conditions - sends flow to different paths (true/false)'}
                  {selectedNode.type === 'delay' && 'Adds a time delay before proceeding to the next step in the workflow'}
                  {selectedNode.type === 'http' && 'Makes HTTP requests to external APIs to fetch or send data'}
                  {selectedNode.type === 'loop' && 'Iterates over arrays or objects, processing each item one by one'}
                  {selectedNode.type === 'transform' && 'Transforms and maps data from one structure to another'}
                  {selectedNode.type === 'variable' && 'Sets variables that can be used later in the workflow using expressions'}
                  {selectedNode.type === 'error' && 'Handles errors from previous nodes with retry logic or continues on error'}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  className="btn-icon btn-danger"
                  onClick={() => handleDeleteNode(selectedNode)}
                  title="Delete Node (or press Delete key)"
                >
                  <FaTrash />
                </button>
                <button className="btn-icon" onClick={() => setShowNodeConfig(false)}>
                  <FaTimes />
                </button>
              </div>
            </div>
            <div className="node-config-body">
              {selectedNode.type === 'trigger' && (
                <div className="node-config-form">
                  <div className="form-group">
                    <label>Trigger Type *</label>
                    <select
                      value={selectedNode.data.triggerType || selectedNode.data.platform || 'manual'}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, triggerType: e.target.value, platform: e.target.value } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, triggerType: e.target.value, platform: e.target.value } });
                      }}
                    >
                      <option value="manual">Manual - Triggered manually from admin panel</option>
                      <option value="whatsapp">WhatsApp - When a message is received on WhatsApp</option>
                      <option value="instagram">Instagram - When a message is received on Instagram</option>
                      <option value="facebook">Facebook - When a message is received on Facebook</option>
                      <option value="google">Google Ads - When a Google Ads event occurs</option>
                      <option value="webhook">Webhook - When an external service sends a webhook</option>
                      <option value="schedule">Schedule - Runs on a specific schedule</option>
                      <option value="cron">Cron Schedule - Runs based on cron expression (e.g., daily at 9 AM)</option>
                      <option value="interval">Interval - Runs at regular intervals (e.g., every hour)</option>
                    </select>
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> Defines when and how the workflow starts. For platform triggers (WhatsApp, Instagram, etc.), the workflow will automatically start when a message is received. Manual triggers require you to start the workflow from the admin panel.
                    </small>
                  </div>
                  {(selectedNode.data.triggerType === 'whatsapp' || selectedNode.data.platform === 'whatsapp') && aiConfig?.whatsapp && (
                    <div className="form-group">
                      <label>AI Config Status</label>
                      <div className="config-status">
                        {aiConfig.whatsapp.accessToken ? (
                          <span className="status-connected">✓ Connected</span>
                        ) : (
                          <span className="status-disconnected">✗ Not Configured</span>
                        )}
                        <a href="/ai-config" target="_blank" className="config-link">
                          Configure <FaEdit />
                        </a>
                      </div>
                    </div>
                  )}
                  {(selectedNode.data.triggerType === 'instagram' || selectedNode.data.triggerType === 'facebook') && aiConfig?.meta && (
                    <div className="form-group">
                      <label>Meta AI Config Status</label>
                      <div className="config-status">
                        {aiConfig.meta.accessToken ? (
                          <span className="status-connected">✓ Connected</span>
                        ) : (
                          <span className="status-disconnected">✗ Not Configured</span>
                        )}
                        <a href="/ai-config" target="_blank" className="config-link">
                          Configure <FaEdit />
                        </a>
                      </div>
                      {selectedNode.data.triggerType === 'instagram' && (
                        <div className="form-group" style={{ marginTop: '10px' }}>
                          <label>Instagram Account ID (Optional)</label>
                          <input
                            type="text"
                            value={selectedNode.data.instagramAccountId || aiConfig.meta.instagramAccountId || ''}
                            onChange={(e) => {
                              const newNodes = nodes.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, instagramAccountId: e.target.value } }
                                  : n
                              );
                              setNodes(newNodes);
                              setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, instagramAccountId: e.target.value } });
                            }}
                            placeholder={aiConfig.meta.instagramAccountId || 'Enter Instagram Account ID'}
                          />
                          <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                            <strong>What this does:</strong> Your Instagram Business Account ID. If left empty, uses the ID from AI Configuration Manager. This identifies which Instagram account should trigger the workflow.
                          </small>
                        </div>
                      )}
                      {selectedNode.data.triggerType === 'facebook' && (
                        <div className="form-group" style={{ marginTop: '10px' }}>
                          <label>Facebook Page ID (Optional)</label>
                          <input
                            type="text"
                            value={selectedNode.data.facebookPageId || aiConfig.meta.facebookPageId || ''}
                            onChange={(e) => {
                              const newNodes = nodes.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, facebookPageId: e.target.value } }
                                  : n
                              );
                              setNodes(newNodes);
                              setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, facebookPageId: e.target.value } });
                            }}
                            placeholder={aiConfig.meta.facebookPageId || 'Enter Facebook Page ID'}
                          />
                          <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                            <strong>What this does:</strong> Your Facebook Page ID. If left empty, uses the ID from AI Configuration Manager. This identifies which Facebook page should trigger the workflow.
                          </small>
                        </div>
                      )}
                    </div>
                  )}
                  {(selectedNode.data.triggerType === 'google' || selectedNode.data.platform === 'google') && aiConfig?.google && (
                    <div className="form-group">
                      <label>AI Config Status</label>
                      <div className="config-status">
                        {aiConfig.google.refreshToken ? (
                          <span className="status-connected">✓ Connected</span>
                        ) : (
                          <span className="status-disconnected">✗ Not Configured</span>
                        )}
                        <a href="/ai-config" target="_blank" className="config-link">
                          Configure <FaEdit />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedNode.type === 'llm' && (
                <div className="node-config-form">
                  <div className="form-group">
                    <label>OpenRouter Configuration</label>
                    <div style={{ padding: '10px', background: '#e7f3ff', borderRadius: '4px', marginBottom: '10px' }}>
                      <strong>Using OpenRouter from AI Configuration Manager</strong>
                      <br />
                      <small>API credentials are managed in <a href="/ai-config" target="_blank">AI Configuration Manager</a></small>
                    </div>
                    {aiConfig?.openrouter && (
                      <div style={{ padding: '8px', background: '#f5f5f5', borderRadius: '4px', marginBottom: '10px' }}>
                        <small>
                          <strong>Base URL:</strong> {aiConfig.openrouter.baseUrl || 'https://openrouter.ai/api/v1'}<br />
                          <strong>Default Model:</strong> {aiConfig.openrouter.defaultModel || 'openai/gpt-3.5-turbo'}<br />
                          <strong>API Key:</strong> {aiConfig.openrouter.apiKey ? '✓ Configured' : '✗ Not Configured'}
                        </small>
                      </div>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Model Selection (Optional)</label>
                    <small style={{ display: 'block', marginBottom: '10px', color: '#666' }}>
                      Default: <strong>{aiConfig?.openrouter?.defaultModel || 'openai/gpt-3.5-turbo'}</strong>
                      <br />
                      Select a specific model for this node, or leave empty to use workflow's model selection.
                    </small>
                    <select
                      value={selectedNode.data.model || ''}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, model: e.target.value } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, model: e.target.value } });
                      }}
                      style={{ width: '100%', padding: '8px' }}
                    >
                      <option value="">Use workflow default/model selection</option>
                      <optgroup label="OpenAI Models">
                        <option value="openai/gpt-4">GPT-4</option>
                        <option value="openai/gpt-4-turbo">GPT-4 Turbo</option>
                        <option value="openai/gpt-3.5-turbo">GPT-3.5 Turbo</option>
                        <option value="openai/gpt-4o">GPT-4o</option>
                        <option value="openai/gpt-4o-mini">GPT-4o Mini</option>
                      </optgroup>
                      <optgroup label="Anthropic Models">
                        <option value="anthropic/claude-3-opus">Claude 3 Opus</option>
                        <option value="anthropic/claude-3-sonnet">Claude 3 Sonnet</option>
                        <option value="anthropic/claude-3-haiku">Claude 3 Haiku</option>
                        <option value="anthropic/claude-3-5-sonnet">Claude 3.5 Sonnet</option>
                      </optgroup>
                      <optgroup label="Google Models">
                        <option value="google/gemini-pro">Gemini Pro</option>
                        <option value="google/gemini-pro-1.5">Gemini Pro 1.5</option>
                        <option value="google/gemini-flash-1.5">Gemini Flash 1.5</option>
                      </optgroup>
                      <optgroup label="Other Models">
                        <option value="meta-llama/llama-3-70b-instruct">Llama 3 70B</option>
                        <option value="mistralai/mistral-large">Mistral Large</option>
                      </optgroup>
                    </select>
                    <small>Select a specific model for this LLM node, or leave empty to use the workflow's model selection.</small>
                  </div>
                  <div className="form-group">
                    <label>Prompt (Optional)</label>
                    <div style={{ position: 'relative' }}>
                      <textarea
                        value={selectedNode.data.prompt || ''}
                        onChange={(e) => {
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, prompt: e.target.value } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, prompt: e.target.value } });
                        }}
                        rows="4"
                        placeholder='Example: The customer said: {{$json.message}}. Customer name: {{$node["Transform Node"].json.customerName}}. Generate a helpful response.'
                      />
                      <button
                        type="button"
                        className="expression-helper-btn"
                        style={{ position: 'absolute', top: '5px', right: '5px' }}
                        onClick={() => setExpressionHelper({ 
                          show: true, 
                          fieldRef: {
                            onChange: (e) => {
                              const newNodes = nodes.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, prompt: e.target.value } }
                                  : n
                              );
                              setNodes(newNodes);
                              setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, prompt: e.target.value } });
                            }
                          },
                          fieldType: 'prompt'
                        })}
                        title="Insert expression from previous nodes"
                      >
                        <FaTerminal /> Insert
                      </button>
                    </div>
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> Custom instructions for the AI in this node. This overrides the workflow's system prompt. If left empty, the workflow's system prompt will be used. You can use expressions like {'{{$json.message}}'}, {'{{$node["NodeName"].json.field}}'}, or {'{{$variables.varName}}'} to include data from previous nodes.
                    </small>
                  </div>
                </div>
              )}

              {selectedNode.type === 'action' && (
                <div className="node-config-form">
                  <div className="form-group">
                    <label>Action Type *</label>
                    <select
                      value={selectedNode.data.actionType || 'send_message'}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, actionType: e.target.value } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, actionType: e.target.value } });
                      }}
                    >
                      <option value="send_message">Send Message</option>
                      <option value="send_facebook_post">Post to Facebook</option>
                      <option value="send_instagram_post">Post to Instagram</option>
                      <option value="send_whatsapp">Send WhatsApp</option>
                    </select>
                  </div>
                  {(selectedNode.data.actionType === 'send_message' || selectedNode.data.actionType === 'send_whatsapp') && (
                    <div className="form-group">
                      <label>Message Content *</label>
                      <textarea
                        value={selectedNode.data.message || ''}
                        onChange={(e) => {
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, message: e.target.value } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, message: e.target.value } });
                        }}
                        rows="4"
                        placeholder="Example: Hello! Thank you for your message. How can I help you today?"
                      />
                      <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                        <strong>What this does:</strong> The message text that will be sent to the user. You can use expressions like {'{{$json.message}}'} to include data from previous nodes, or {'{{$variables.customVar}}'} to use workflow variables.
                      </small>
                    </div>
                  )}
                  {(selectedNode.data.actionType === 'send_facebook_post' || selectedNode.data.actionType === 'send_instagram_post') && (
                    <div className="form-group">
                      <label>Post Content *</label>
                      <textarea
                        value={selectedNode.data.postContent || selectedNode.data.message || ''}
                        onChange={(e) => {
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, postContent: e.target.value, message: e.target.value } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, postContent: e.target.value, message: e.target.value } });
                        }}
                        rows="4"
                        placeholder="Enter post content..."
                      />
                      <small>Content for the social media post.</small>
                    </div>
                  )}
                  <div className="form-group">
                    <label>Platform *</label>
                    <select
                      value={selectedNode.data.platform || 'whatsapp'}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, platform: e.target.value } }
                            : n
                          );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, platform: e.target.value } });
                      }}
                    >
                      <option value="whatsapp">WhatsApp - Send via WhatsApp Business API</option>
                      <option value="facebook">Facebook - Send via Facebook Messenger</option>
                      <option value="instagram">Instagram - Send via Instagram Direct Message</option>
                      <option value="google">Google - Send via Google services</option>
                    </select>
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> Selects which platform to send the message through. Make sure the platform is configured in AI Configuration Manager.
                    </small>
                  </div>
                  {selectedNode.data.platform && aiConfig && (
                    <div className="form-group">
                      <label>AI Config Status</label>
                      <div className="config-status">
                        {(selectedNode.data.platform === 'whatsapp' && aiConfig.whatsapp?.accessToken) ||
                         ((selectedNode.data.platform === 'facebook' || selectedNode.data.platform === 'instagram') && aiConfig.meta?.accessToken) ||
                         (selectedNode.data.platform === 'google' && aiConfig.google?.refreshToken) ? (
                          <span className="status-connected">✓ Connected</span>
                        ) : (
                          <span className="status-disconnected">✗ Not Configured</span>
                        )}
                        <a href="/ai-config" target="_blank" className="config-link">
                          Configure <FaEdit />
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {selectedNode.type === 'condition' && (
                <div className="node-config-form">
                  <div className="form-group">
                    <label>Condition Name</label>
                    <input
                      type="text"
                      value={selectedNode.data.conditionName || 'Condition 1'}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, conditionName: e.target.value } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, conditionName: e.target.value } });
                      }}
                      placeholder="Condition 1"
                    />
                  </div>
                  <div className="form-group">
                    <label>Condition Expression (Advanced)</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={selectedNode.data.conditionExpression || ''}
                        onChange={(e) => {
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, conditionExpression: e.target.value } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, conditionExpression: e.target.value } });
                        }}
                        placeholder='e.g., {{$json.message.includes("urgent")}} or {{$node["LLM Node"].json.sentiment === "positive"}}'
                        style={{ paddingRight: '140px' }}
                      />
                      <button
                        type="button"
                        className="expression-helper-btn"
                        onClick={() => setExpressionHelper({ 
                          show: true, 
                          fieldRef: {
                            onChange: (e) => {
                              const newNodes = nodes.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, conditionExpression: e.target.value } }
                                  : n
                              );
                              setNodes(newNodes);
                              setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, conditionExpression: e.target.value } });
                            }
                          },
                          fieldType: 'condition'
                        })}
                        title="Insert expression from previous nodes"
                      >
                        <FaTerminal /> Insert
                      </button>
                    </div>
                    <small>Use expressions like {'{{$json.field}}'}, {'{{$node["NodeName"].json.field}}'}, or {'{{$variables.varName}}'}. Leave empty to use simple condition logic below.</small>
                  </div>
                  <div className="form-group">
                    <label>Condition Logic (Simple)</label>
                    <input
                      type="text"
                      value={selectedNode.data.condition || ''}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, condition: e.target.value } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, condition: e.target.value } });
                      }}
                      placeholder="e.g., message contains hello"
                    />
                    <small>Simple text-based condition. Used if expression is empty.</small>
                  </div>
                  <div className="form-group">
                    <label>Condition Status</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <button
                        className={`btn ${selectedNode.data.isActive !== false ? 'btn-success' : 'btn-secondary'}`}
                        onClick={() => {
                          const isActive = selectedNode.data.isActive !== false;
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, isActive: !isActive } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, isActive: !isActive } });
                        }}
                        style={{ flex: 1 }}
                      >
                        {selectedNode.data.isActive !== false ? '✓ Active' : '✗ Inactive'}
                      </button>
                      <button
                        className={`btn ${selectedNode.data.isActive === false ? 'btn-danger' : 'btn-secondary'}`}
                        onClick={() => {
                          const isActive = selectedNode.data.isActive !== false;
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, isActive: !isActive } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, isActive: !isActive } });
                        }}
                        style={{ flex: 1 }}
                      >
                        {selectedNode.data.isActive === false ? '✗ Inactive' : 'Make Inactive'}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {selectedNode.type === 'delay' && (
                <div className="node-config-form">
                  <div className="form-group">
                    <label>Delay (seconds) *</label>
                    <input
                      type="number"
                      value={selectedNode.data.delay || 0}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, delay: parseInt(e.target.value) || 0 } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, delay: parseInt(e.target.value) || 0 } });
                      }}
                      min="0"
                      placeholder="e.g., 5 for 5 seconds, 60 for 1 minute"
                    />
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> Pauses the workflow for the specified number of seconds before proceeding to the next node. Useful for rate limiting, waiting for external processes, or creating delays between actions.
                    </small>
                  </div>
                </div>
              )}

              {selectedNode.type === 'http' && (
                <div className="node-config-form">
                  <div className="form-group">
                    <label>HTTP Method *</label>
                    <select
                      value={selectedNode.data.method || 'GET'}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, method: e.target.value } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, method: e.target.value } });
                      }}
                    >
                      <option value="GET">GET - Retrieve data from the API</option>
                      <option value="POST">POST - Send data to create a new resource</option>
                      <option value="PUT">PUT - Update an existing resource</option>
                      <option value="DELETE">DELETE - Delete a resource</option>
                      <option value="PATCH">PATCH - Partially update a resource</option>
                    </select>
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> The HTTP method determines the type of request. GET is for fetching data, POST for creating, PUT/PATCH for updating, DELETE for removing.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>API URL *</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={selectedNode.data.url || ''}
                        onChange={(e) => {
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, url: e.target.value } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, url: e.target.value } });
                        }}
                        placeholder='Example: {{$variables.apiBaseUrl}}/users/{{$json.userId}} or {{$node["Variable Node"].json.apiUrl}}/endpoint'
                        style={{ paddingRight: '140px' }}
                      />
                      <button
                        type="button"
                        className="expression-helper-btn"
                        onClick={() => setExpressionHelper({ 
                          show: true, 
                          fieldRef: {
                            onChange: (e) => {
                              const newNodes = nodes.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, url: e.target.value } }
                                  : n
                              );
                              setNodes(newNodes);
                              setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, url: e.target.value } });
                            }
                          },
                          fieldType: 'url'
                        })}
                        title="Insert expression from previous nodes"
                      >
                        <FaTerminal /> Insert
                      </button>
                    </div>
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> The full URL of the API endpoint to call. You can use expressions like {'{{$json.apiUrl}}'}, {'{{$node["NodeName"].json.field}}'}, or {'{{$variables.baseUrl}}'} to make it dynamic based on previous node data or variables.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Headers (JSON) - Optional</label>
                    <textarea
                      value={typeof selectedNode.data.headers === 'object' ? JSON.stringify(selectedNode.data.headers, null, 2) : (selectedNode.data.headers || '{}')}
                      onChange={(e) => {
                        try {
                          const headers = JSON.parse(e.target.value);
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, headers } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, headers } });
                        } catch (err) {
                          // Invalid JSON, keep as string
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, headers: e.target.value } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, headers: e.target.value } });
                        }
                      }}
                      rows="4"
                      placeholder='Example: {"Content-Type": "application/json", "Authorization": "Bearer {{$variables.apiToken}}", "X-Custom-Header": "{{$json.customValue}}"}'
                    />
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> HTTP headers to send with the request. Must be valid JSON. Use expressions like {'{{$variables.apiToken}}'} for dynamic values. Common headers: Authorization, Content-Type, API keys.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Request Body (for POST/PUT/PATCH) - Optional</label>
                    <div style={{ position: 'relative' }}>
                      <textarea
                        value={selectedNode.data.body || ''}
                        onChange={(e) => {
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, body: e.target.value } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, body: e.target.value } });
                        }}
                        rows="5"
                        placeholder='Example: {"name": "{{$json.userName}}", "email": "{{$json.email}}", "userId": "{{$node["HTTP Node"].json.data.id}}"}'
                      />
                      <button
                        type="button"
                        className="expression-helper-btn"
                        style={{ position: 'absolute', top: '5px', right: '5px' }}
                        onClick={() => setExpressionHelper({ 
                          show: true, 
                          fieldRef: {
                            onChange: (e) => {
                              const newNodes = nodes.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, body: e.target.value } }
                                  : n
                              );
                              setNodes(newNodes);
                              setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, body: e.target.value } });
                            }
                          },
                          fieldType: 'body'
                        })}
                        title="Insert expression from previous nodes"
                      >
                        <FaTerminal /> Insert
                      </button>
                    </div>
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> Data to send in the request body (for POST, PUT, PATCH methods). Can be JSON string or use expressions like {'{{$json.field}}'}, {'{{$node["NodeName"].json.field}}'}, or {'{{$variables.varName}}'} to include data from previous nodes. Example: {'{"userId": "{{$json.id}}", "action": "update"}'}
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Timeout (milliseconds)</label>
                    <input
                      type="number"
                      min="1000"
                      value={selectedNode.data.timeout || 30000}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, timeout: parseInt(e.target.value) || 30000 } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, timeout: parseInt(e.target.value) || 30000 } });
                      }}
                      placeholder="30000 (30 seconds)"
                    />
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> Maximum time to wait for the API response before timing out. Default is 30000ms (30 seconds). Increase for slow APIs, decrease for faster responses.
                    </small>
                  </div>
                </div>
              )}

              {selectedNode.type === 'loop' && (
                <div className="node-config-form">
                  <div className="form-group">
                    <label>Loop Type *</label>
                    <select
                      value={selectedNode.data.loopType || 'array'}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, loopType: e.target.value } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, loopType: e.target.value } });
                      }}
                    >
                      <option value="array">Array Loop - Process each item in an array one by one</option>
                      <option value="object">Object Loop - Process each key-value pair in an object</option>
                      <option value="while">While Loop - Keep looping while a condition is true</option>
                    </select>
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> Defines how the loop works. Array loop processes each item in a list, Object loop processes each property, While loop continues until condition is false.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Input Data *</label>
                    <input
                      type="text"
                      value={selectedNode.data.input || ''}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, input: e.target.value } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, input: e.target.value } });
                      }}
                      placeholder="Example: {{$json.items}} or {{$json.users}} or {{$node['HTTP Node'].json.data.results}}"
                    />
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> Expression pointing to the array or object to iterate over. Use {'{{$json.field}}'} to access data from previous nodes. The loop will process each item in this data.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={selectedNode.data.continueOnError || false}
                        onChange={(e) => {
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, continueOnError: e.target.checked } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, continueOnError: e.target.checked } });
                        }}
                      />
                      Continue on Error
                    </label>
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> If checked, the loop will continue processing remaining items even if one iteration fails. If unchecked, the loop stops on the first error.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Max Iterations (Optional)</label>
                    <input
                      type="number"
                      min="1"
                      value={selectedNode.data.maxIterations || ''}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, maxIterations: e.target.value ? parseInt(e.target.value) : null } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, maxIterations: e.target.value ? parseInt(e.target.value) : null } });
                      }}
                      placeholder="Example: 100 (process max 100 items)"
                    />
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> Limits the maximum number of iterations. Useful for preventing infinite loops or processing only a subset of data. Leave empty to process all items.
                    </small>
                  </div>
                </div>
              )}

              {selectedNode.type === 'transform' && (
                <div className="node-config-form">
                  <div className="form-group">
                    <label>Input Data - Optional</label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="text"
                        value={selectedNode.data.input || ''}
                        onChange={(e) => {
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, input: e.target.value } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, input: e.target.value } });
                        }}
                        placeholder='Example: {{$json.rawData}} or {{$node["HTTP Node"].json.data}} or {{$variables.myData}}'
                        style={{ paddingRight: '140px' }}
                      />
                      <button
                        type="button"
                        className="expression-helper-btn"
                        onClick={() => setExpressionHelper({ 
                          show: true, 
                          fieldRef: {
                            onChange: (e) => {
                              const newNodes = nodes.map(n =>
                                n.id === selectedNode.id
                                  ? { ...n, data: { ...n.data, input: e.target.value } }
                                  : n
                              );
                              setNodes(newNodes);
                              setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, input: e.target.value } });
                            }
                          },
                          fieldType: 'input'
                        })}
                        title="Insert expression from previous nodes"
                      >
                        <FaTerminal /> Insert
                      </button>
                    </div>
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> Expression pointing to the data you want to transform. If empty, uses data from the previous node. Use {'{{$json.field}}'}, {'{{$node["NodeName"].json.field}}'}, or {'{{$variables.varName}}'} to access specific fields from previous nodes.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Field Mapping (JSON) *</label>
                    <textarea
                      value={typeof selectedNode.data.mapping === 'object' ? JSON.stringify(selectedNode.data.mapping, null, 2) : (selectedNode.data.mapping || '{}')}
                      onChange={(e) => {
                        try {
                          const mapping = JSON.parse(e.target.value);
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, mapping } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, mapping } });
                        } catch (err) {
                          // Invalid JSON, keep as string
                          const newNodes = nodes.map(n =>
                            n.id === selectedNode.id
                              ? { ...n, data: { ...n.data, mapping: e.target.value } }
                              : n
                          );
                          setNodes(newNodes);
                          setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, mapping: e.target.value } });
                        }
                      }}
                      rows="8"
                      placeholder='Example: {"fullName": "{{$json.firstName}} {{$json.lastName}}", "email": "{{$json.email}}", "age": "{{Math.max(0, $json.age)}}", "formattedPrice": "${{$json.price.toFixed(2)}}"}'
                    />
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> Maps input data to output fields. Each key is the output field name, and the value is an expression that extracts/transforms data. Examples: Combine fields ({'"fullName": "{{$json.firstName}} {{$json.lastName}}"'}), use functions ({'"age": "{{Math.max(0, $json.age)}}"'}), or format data ({'"price": "${{$json.price.toFixed(2)}}"'}).
                    </small>
                  </div>
                </div>
              )}

              {selectedNode.type === 'variable' && (
                <div className="node-config-form">
                  <div className="form-group">
                    <label>Variable Name *</label>
                    <input
                      type="text"
                      value={selectedNode.data.variableName || ''}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, variableName: e.target.value } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, variableName: e.target.value } });
                      }}
                      placeholder="Examples: apiToken, userId, customerName, apiBaseUrl"
                    />
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> The name of the variable to store. Use descriptive names without spaces (e.g., "apiToken", "userId"). You can access this variable later in any node using {'{{$variables.variableName}}'}.
                    </small>
                  </div>
                  <div className="form-group">
                    <label>Variable Value *</label>
                    <textarea
                      value={selectedNode.data.variableValue || ''}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, variableValue: e.target.value } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, variableValue: e.target.value } });
                      }}
                      rows="4"
                      placeholder='Examples: {{$json.token}} or {{$node["HTTP Node"].json.data.apiKey}} or "static-value" or {{$variables.otherVar}}'
                    />
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> The value to store in the variable. Can be a static value (like "production") or an expression that extracts data from previous nodes. Examples: {'{{$json.token}}'} (from previous node), {'{{$node["HTTP Node"].json.data.apiKey}}'} (from specific node), or {'{{$variables.otherVar}}'} (from another variable).
                    </small>
                  </div>
                </div>
              )}

              {selectedNode.type === 'error' && (
                <div className="node-config-form">
                  <div className="form-group">
                    <label>Error Action *</label>
                    <select
                      value={selectedNode.data.errorAction || 'throw'}
                      onChange={(e) => {
                        const newNodes = nodes.map(n =>
                          n.id === selectedNode.id
                            ? { ...n, data: { ...n.data, errorAction: e.target.value } }
                            : n
                        );
                        setNodes(newNodes);
                        setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, errorAction: e.target.value } });
                      }}
                    >
                      <option value="throw">Throw Error (Stop) - Stop workflow execution on error</option>
                      <option value="continue">Continue on Error - Ignore error and continue to next node</option>
                      <option value="retry">Retry with Backoff - Retry the failed operation multiple times</option>
                    </select>
                    <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                      <strong>What this does:</strong> Defines how to handle errors from previous nodes. "Throw" stops the workflow, "Continue" ignores errors and proceeds, "Retry" attempts the operation again with delays.
                    </small>
                  </div>
                  {selectedNode.data.errorAction === 'retry' && (
                    <>
                      <div className="form-group">
                        <label>Max Retries *</label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={selectedNode.data.maxRetries || 3}
                          onChange={(e) => {
                            const newNodes = nodes.map(n =>
                              n.id === selectedNode.id
                                ? { ...n, data: { ...n.data, maxRetries: parseInt(e.target.value) || 3 } }
                                : n
                            );
                            setNodes(newNodes);
                            setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, maxRetries: parseInt(e.target.value) || 3 } });
                          }}
                          placeholder="3"
                        />
                        <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                          <strong>What this does:</strong> Maximum number of retry attempts before giving up. Recommended: 3-5 retries for transient errors.
                        </small>
                      </div>
                      <div className="form-group">
                        <label>Retry Delay (milliseconds) *</label>
                        <input
                          type="number"
                          min="100"
                          value={selectedNode.data.retryDelay || 1000}
                          onChange={(e) => {
                            const newNodes = nodes.map(n =>
                              n.id === selectedNode.id
                                ? { ...n, data: { ...n.data, retryDelay: parseInt(e.target.value) || 1000 } }
                                : n
                            );
                            setNodes(newNodes);
                            setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, retryDelay: parseInt(e.target.value) || 1000 } });
                          }}
                          placeholder="1000 (1 second)"
                        />
                        <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                          <strong>What this does:</strong> Initial delay before retrying (in milliseconds). Uses exponential backoff - each retry waits longer (1s, 2s, 4s, etc.). Default: 1000ms (1 second).
                        </small>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Node Label (Optional)</label>
                <input
                  type="text"
                  value={selectedNode.data.label || ''}
                  onChange={(e) => {
                    const newNodes = nodes.map(n =>
                      n.id === selectedNode.id
                        ? { ...n, data: { ...n.data, label: e.target.value } }
                        : n
                    );
                    setNodes(newNodes);
                    setSelectedNode({ ...selectedNode, data: { ...selectedNode.data, label: e.target.value } });
                  }}
                  placeholder="Example: 'Send Welcome Message' or 'Check User Status'"
                />
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  <strong>What this does:</strong> A custom name/label for this node that appears on the canvas. Helps identify the node's purpose at a glance. If empty, uses the default node type name.
                </small>
              </div>
            </div>
            <div className="node-config-footer">
              <button className="btn btn-primary" onClick={() => setShowNodeConfig(false)}>
                Save
              </button>
              <button className="btn btn-secondary" onClick={() => setShowNodeConfig(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Final Response Display */}
      {finalResponse && (
        <div className="final-response-panel">
          <div className="final-response-header">
            <h3>📤 Final Response</h3>
            <button className="btn-icon" onClick={() => { setFinalResponse(null); setWorkflowExecutionStatus(null); }}>
              <FaTimes />
            </button>
          </div>
          <div className="final-response-body">
            {workflowExecutionStatus === 'executing' && (
              <div className="loading">Executing workflow...</div>
            )}
            {workflowExecutionStatus === 'completed' && (
              <div className="response-content">
                <div className="response-section">
                  <h4>Status: <span className="status-success">✓ Completed</span></h4>
                  <pre>{JSON.stringify(finalResponse, null, 2)}</pre>
                </div>
              </div>
            )}
            {workflowExecutionStatus === 'failed' && (
              <div className="response-content">
                <div className="response-section">
                  <h4>Status: <span className="status-error">✗ Failed</span></h4>
                  <pre>{JSON.stringify(finalResponse, null, 2)}</pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Final Response Display */}
      {finalResponse && (
        <div className="final-response-panel">
          <div className="final-response-header">
            <h3>📤 Final Response</h3>
            <button className="btn-icon" onClick={() => { setFinalResponse(null); setWorkflowExecutionStatus(null); }}>
              <FaTimes />
            </button>
          </div>
          <div className="final-response-body">
            {workflowExecutionStatus === 'executing' && (
              <div className="loading">Executing workflow...</div>
            )}
            {workflowExecutionStatus === 'completed' && (
              <div className="response-content">
                <div className="response-section">
                  <h4>Status: <span className="status-success">✓ Completed</span></h4>
                  <pre>{JSON.stringify(finalResponse, null, 2)}</pre>
                </div>
              </div>
            )}
            {workflowExecutionStatus === 'failed' && (
              <div className="response-content">
                <div className="response-section">
                  <h4>Status: <span className="status-error">✗ Failed</span></h4>
                  <pre>{JSON.stringify(finalResponse, null, 2)}</pre>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edge Delete Confirmation */}
      {showEdgeDeleteConfirm && selectedEdge && (
        <div className="edge-delete-overlay" onClick={() => setShowEdgeDeleteConfirm(false)}>
          <div className="edge-delete-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Connection?</h3>
            <p>Are you sure you want to delete this connection between nodes?</p>
            <div className="edge-delete-actions">
              <button
                className="btn btn-danger"
                onClick={() => {
                  // Mark as confirmed and delete
                  setConfirmedDeletions(prev => new Set(prev).add(selectedEdge.id));
                  const newEdges = edges.filter(e => e.id !== selectedEdge.id);
                  setEdges(newEdges);
                  setSelectedEdge(null);
                  setPendingEdgeDeletion(null);
                  setShowEdgeDeleteConfirm(false);
                }}
              >
                Delete
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  // Edge is already restored, just close modal
                  setSelectedEdge(null);
                  setPendingEdgeDeletion(null);
                  setShowEdgeDeleteConfirm(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false, title: '', message: '', onConfirm: null, type: 'danger' })}
        confirmText="Delete"
        cancelText="Cancel"
        type={confirmModal.type}
      />

      {/* Validation Info Modal */}
      {showValidationModal && (
        <div className="validation-modal-overlay" onClick={() => setShowValidationModal(false)}>
          <div className="validation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="validation-modal-header">
              <h3>
                <FaInfoCircle style={{ marginRight: '10px', color: '#ff9800' }} />
                Workflow Validation
              </h3>
              <button className="btn-icon" onClick={() => setShowValidationModal(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="validation-modal-body">
              {workflowValidation.valid ? (
                <div style={{ padding: '20px', textAlign: 'center', color: '#4caf50' }}>
                  <FaInfoCircle style={{ fontSize: '48px', marginBottom: '10px' }} />
                  <h4>Workflow is Valid!</h4>
                  <p>All nodes are properly configured and the workflow is ready to execute.</p>
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: '20px', padding: '15px', background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                    <strong style={{ color: '#856404', display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                      <FaExclamationCircle style={{ marginRight: '8px' }} />
                      Validation Errors Found
                    </strong>
                    <p style={{ color: '#856404', margin: 0 }}>
                      Please fix the following issues before executing the workflow:
                    </p>
                  </div>
                  
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <h4 style={{ marginBottom: '15px', color: '#333' }}>General Errors:</h4>
                    <ul style={{ marginBottom: '20px', paddingLeft: '20px' }}>
                      {workflowValidation.errors?.filter(error => 
                        !error.includes('node "') && !error.includes('Trigger node') && !error.includes('Action node') && 
                        !error.includes('LLM node') && !error.includes('Condition node') && !error.includes('Delay node')
                      ).map((error, index) => (
                        <li key={index} style={{ marginBottom: '8px', color: '#dc3545' }}>
                          {error}
                        </li>
                      ))}
                    </ul>

                    <h4 style={{ marginBottom: '15px', color: '#333' }}>Node-Specific Errors:</h4>
                    {Array.from(nodeErrors.entries()).map(([nodeId, errors]) => {
                      const node = nodes.find(n => n.id === nodeId);
                      return (
                        <div key={nodeId} style={{ marginBottom: '15px', padding: '12px', background: '#f8f9fa', borderRadius: '6px', borderLeft: '4px solid #dc3545' }}>
                          <strong style={{ color: '#dc3545', display: 'block', marginBottom: '8px' }}>
                            {node?.data?.label || node?.type || 'Node'} ({node?.type || 'unknown'})
                          </strong>
                          <ul style={{ margin: 0, paddingLeft: '20px' }}>
                            {errors.map((error, index) => (
                              <li key={index} style={{ marginBottom: '5px', color: '#721c24' }}>
                                {error}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <div className="validation-modal-footer">
              <button className="btn btn-primary" onClick={() => setShowValidationModal(false)}>
                Close
              </button>
              {!workflowValidation.valid && nodeErrors.size > 0 && (
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowValidationModal(false);
                    // Scroll to first error node if possible
                    const firstErrorNodeId = Array.from(nodeErrors.keys())[0];
                    if (firstErrorNodeId) {
                      const node = nodes.find(n => n.id === firstErrorNodeId);
                      if (node) {
                        // Try to select the node
                        setSelectedNode(node);
                        setShowNodeConfig(true);
                      }
                    }
                  }}
                >
                  Go to First Error
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Workflow Chat Modal */}
      {showChat && selectedWorkflow?._id && (
        <WorkflowChat 
          workflowId={selectedWorkflow._id} 
          onClose={() => setShowChat(false)} 
        />
      )}

      {/* Node Output Modal */}
      {showNodeOutput && nodeOutputs.has(showNodeOutput) && (
        <div className="modal-overlay" onClick={() => setShowNodeOutput(null)}>
          <div className="modal-content" style={{ maxWidth: '800px', maxHeight: '80vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <FaInfoCircle style={{ marginRight: '10px' }} />
                Node Output: {nodes.find(n => n.id === showNodeOutput)?.data?.label || showNodeOutput}
              </h3>
              <button className="btn-icon" onClick={() => setShowNodeOutput(null)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              {(() => {
                const output = nodeOutputs.get(showNodeOutput);
                const node = nodes.find(n => n.id === showNodeOutput);
                return (
                  <div>
                    <div style={{ marginBottom: '20px', padding: '15px', background: output.success ? '#d4edda' : '#f8d7da', borderRadius: '8px', border: `1px solid ${output.success ? '#c3e6cb' : '#f5c6cb'}` }}>
                      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                        <strong style={{ color: output.success ? '#155724' : '#721c24', fontSize: '16px' }}>
                          {output.success ? '✓ Execution Successful' : '✗ Execution Failed'}
                        </strong>
                        <span style={{ marginLeft: '15px', fontSize: '14px', color: '#666', padding: '4px 12px', background: '#f0f0f0', borderRadius: '4px' }}>
                          Node Type: {output.nodeType || node?.type || 'unknown'}
                        </span>
                      </div>
                      {output.message && (
                        <p style={{ margin: '8px 0', color: output.success ? '#155724' : '#721c24', fontSize: '14px' }}>
                          <strong>Message:</strong> {output.message}
                        </p>
                      )}
                      {output.error && (
                        <p style={{ margin: '8px 0', color: '#721c24', fontSize: '14px' }}>
                          <strong>Error:</strong> {output.error}
                        </p>
                      )}
                    </div>
                    
                    <div>
                      <h4 style={{ marginBottom: '10px', color: '#333' }}>Output Data:</h4>
                      <pre style={{ 
                        background: '#f8f9fa', 
                        padding: '15px', 
                        borderRadius: '8px', 
                        overflow: 'auto',
                        maxHeight: '400px',
                        fontSize: '12px',
                        lineHeight: '1.5',
                        border: '1px solid #e0e0e0'
                      }}>
                        {JSON.stringify(output.data, null, 2)}
                      </pre>
                      <small style={{ display: 'block', marginTop: '8px', color: '#666' }}>
                        This data is available to subsequent nodes using expressions like {'{{$node["' + (node?.data?.label || showNodeOutput) + '"].json.field}}'}
                      </small>
                    </div>
                    
                    {nodeErrors.has(showNodeOutput) && (
                      <div style={{ marginTop: '20px', padding: '15px', background: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                        <strong style={{ color: '#856404', display: 'block', marginBottom: '10px', fontSize: '14px' }}>
                          ⚠️ Configuration Issues Detected:
                        </strong>
                        <ul style={{ margin: 0, paddingLeft: '20px', color: '#856404' }}>
                          {nodeErrors.get(showNodeOutput).map((error, idx) => (
                            <li key={idx} style={{ marginBottom: '5px', fontSize: '13px' }}>{error}</li>
                          ))}
                        </ul>
                        <button
                          className="btn btn-primary btn-sm"
                          style={{ marginTop: '12px' }}
                          onClick={() => {
                            setShowNodeOutput(null);
                            setSelectedNode(node);
                            setShowNodeConfig(true);
                          }}
                        >
                          <FaEdit /> Fix Configuration Now
                        </button>
                      </div>
                    )}
                    
                    {!nodeErrors.has(showNodeOutput) && !output.success && (
                      <div style={{ marginTop: '20px', padding: '15px', background: '#e7f3ff', borderRadius: '8px', border: '1px solid #b3d9ff' }}>
                        <p style={{ margin: 0, color: '#004085', fontSize: '14px' }}>
                          <strong>💡 Tip:</strong> This node executed but returned an error. Check the error message above and verify the node configuration.
                        </p>
                        <button
                          className="btn btn-secondary btn-sm"
                          style={{ marginTop: '10px' }}
                          onClick={() => {
                            setShowNodeOutput(null);
                            setSelectedNode(node);
                            setShowNodeConfig(true);
                          }}
                        >
                          <FaEdit /> Review Configuration
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowNodeOutput(null)}>
                Close
              </button>
              {nodes.find(n => n.id === showNodeOutput) && (
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const node = nodes.find(n => n.id === showNodeOutput);
                    setShowNodeOutput(null);
                    setSelectedNode(node);
                    setShowNodeConfig(true);
                  }}
                >
                  <FaEdit /> Configure Node
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Execution History Modal */}
      {showHistory && selectedWorkflow?._id && (
        <div className="modal-overlay" onClick={() => setShowHistory(false)}>
          <div className="modal-content" style={{ maxWidth: '900px', maxHeight: '80vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <FaHistory style={{ marginRight: '10px' }} />
                Execution History - {selectedWorkflow.name}
              </h3>
              <button className="btn-icon" onClick={() => setShowHistory(false)}>
                <FaTimes />
              </button>
            </div>
            <div className="modal-body">
              {executionHistory.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>
                  <FaHistory style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.5 }} />
                  <p>No execution history yet</p>
                  <small>Execute the workflow to see history here</small>
                </div>
              ) : (
                <div className="execution-history-list">
                  {executionHistory.map((exec, index) => (
                    <div key={index} className="execution-item" style={{ 
                      padding: '1rem', 
                      marginBottom: '1rem', 
                      border: '1px solid #ddd', 
                      borderRadius: '8px',
                      background: exec.success ? '#f0f9ff' : '#fff5f5'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <div>
                          <strong>Execution ID:</strong> {exec.executionId || `Exec ${index + 1}`}
                        </div>
                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                          {exec.success ? (
                            <span style={{ color: '#10b981', fontWeight: 'bold' }}>✓ Success</span>
                          ) : (
                            <span style={{ color: '#ef4444', fontWeight: 'bold' }}>✗ Failed</span>
                          )}
                          <small style={{ color: '#666' }}>
                            {new Date(exec.timestamp).toLocaleString()}
                          </small>
                        </div>
                      </div>
                      {exec.duration && (
                        <div style={{ marginBottom: '0.5rem' }}>
                          <strong>Duration:</strong> {exec.duration}ms
                        </div>
                      )}
                      {exec.steps && exec.steps.length > 0 && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <strong>Steps Executed:</strong> {exec.steps.length}
                          <details style={{ marginTop: '0.5rem' }}>
                            <summary style={{ cursor: 'pointer', color: '#667eea' }}>View Details</summary>
                            <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#f9fafb', borderRadius: '4px', fontSize: '0.85rem' }}>
                              {exec.steps.map((step, stepIndex) => (
                                <div key={stepIndex} style={{ marginBottom: '0.5rem', padding: '0.5rem', borderLeft: '3px solid #667eea' }}>
                                  <strong>{step.nodeType}:</strong> {step.nodeId}
                                  {step.result && (
                                    <div style={{ marginTop: '0.25rem', color: '#666' }}>
                                      {step.result.success ? '✓' : '✗'} {step.result.message || step.result.error || 'Completed'}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          </details>
                        </div>
                      )}
                      {exec.error && (
                        <div style={{ marginTop: '0.5rem', padding: '0.5rem', background: '#fee2e2', borderRadius: '4px', color: '#991b1b' }}>
                          <strong>Error:</strong> {exec.error.message || JSON.stringify(exec.error)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowHistory(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkflowManager;


